import { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, X, ExternalLink, Search, Calendar, RefreshCw, GripHorizontal } from 'lucide-react'
import {
  cn, toStyleObject, safeInvoke, computeDropPosition, toCssLength, toJsonStr, numericSortingFn, OVERLAY_Z_INDEX,
  isSelectTrue, normalizeCellValue, cellValuesEqual, cellToJsonStr, formatFrDate, formatFrDateTime,
  dateToIsoInput, isoInputToDate, dateTimeToIsoInput, isoInputToDateTime, stripHtml, generateId,
  HAS_WEBKIT_DATE_ICON,
} from '@/lib/utils'
import { createGeneralApi, clearListeners } from '../tbsGeneralApi'
import { useT } from '@/lib/i18n'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  flexRender,
} from '@tanstack/react-table'
import styles from './fields.module.css'

// Types dont la doc (tables-arbres.md) ne définit aucun rendu écriture ("Rendu write : —") — même
// avec col.mode === 'write' (ou un override de cellule vers un de ces types), le nœud reste en
// affichage, jamais en TreeCellEditor. `Link` : mode ignoré volontairement (toujours lecture
// seule). Même constante que TableField.jsx.
const READ_ONLY_EDITOR_TYPES = new Set(['Image', 'HTML', 'Link'])

// Fusionne un override de cellule (setCellEditor(colId, json), posé par nodeId+code colonne) par
// dessus la colonne — mêmes clés que côté TableField.jsx (type/maxSize/placeholder/items).
function resolveEffectiveCol(overridesByNodeId, nodeId, col) {
  const override = nodeId != null ? overridesByNodeId[nodeId]?.[col.code] : null
  return override ? { ...col, ...override } : col
}

function PasswordCellDisplay({ value }) {
  const [show, setShow] = useState(false)
  const hasVal = value != null && value !== ''
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {show ? (value ?? '') : (hasVal ? '••••' : '')}
      {hasVal && (
        <button type="button" tabIndex={-1} onClick={() => setShow(v => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: 'hsl(var(--muted-foreground))' }}>
          {show ? <EyeOff size={10} /> : <Eye size={10} />}
        </button>
      )}
    </span>
  )
}

const DRAG_WIDTH   = 36
const ROWNUM_WIDTH = 40

function buildColGroups(items) {
  const groups = []
  let current = null
  for (const item of items) {
    if (item[3]) {
      current = { groupLabel: item[1], items: [] }
      groups.push(current)
    } else {
      if (!current) { current = { groupLabel: null, items: [] }; groups.push(current) }
      current.items.push(item)
    }
  }
  return groups
}

const CELL_CLEAR_BTN = {
  position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
  display: 'flex', alignItems: 'center', color: 'hsl(var(--muted-foreground))',
  lineHeight: 1,
}

const RESIZE_MIN_HEIGHT = 50 // px — poignée de redimensionnement (attribut `resizable`)

const fmtMontant = v => {
  const n = parseFloat(v)
  return isNaN(n) ? (v ?? '') : n.toLocaleString('fr-FR')
}

// Valeur brute d'affichage — Date/DateTime sont des objets Date, non affichables tels quels comme
// enfant React. `value` peut aussi être déjà une chaîne formatée "JJ/MM/AAAA" (valeur de
// groupement — cf. getGroupingValue sur les colDefs Date/DateTime).
function fmtTreeCellValue(colType, value) {
  if (colType === 'Date') return value instanceof Date ? (formatFrDate(value) ?? '') : (value ?? '')
  if (colType === 'DateTime') return value instanceof Date ? (formatFrDateTime(value) ?? '') : (value ?? '')
  if (colType === 'Heure') return value != null ? `${value}h` : ''
  return value ?? ''
}

// Formatage d'une valeur de cellule pour l'affichage dans le pied de tableau (first/last) — Date/
// DateTime sont des objets Date (non affichables tels quels), les autres types passent tels quels.
function fmtSummaryValue(colType, v) {
  if (colType === 'Date') return formatFrDate(v) ?? ''
  if (colType === 'DateTime') return formatFrDateTime(v) ?? ''
  return String(v)
}

function computeSummary(type, rows, colCode, colType) {
  const isNum = ['Numerique', 'Montant'].includes(colType)
  const numVals = () => rows.map(r => parseFloat(r.getValue(colCode))).filter(n => !isNaN(n))
  // Date/DateTime sont des objets Date : deux instances représentant le même instant ne sont pas
  // `===` — dédupliquer sur leur timestamp plutôt que sur la référence.
  const distinctKey = v => v instanceof Date ? v.getTime() : v
  switch (type) {
    case 'count':         return String(rows.length)
    case 'countDistinct': return String(new Set(rows.map(r => distinctKey(r.getValue(colCode)))).size)
    case 'sum': {
      const s = numVals().reduce((a, b) => a + b, 0)
      return isNum ? fmtMontant(s) : String(s)
    }
    case 'min': { const v = numVals(); return v.length ? (isNum ? fmtMontant(Math.min(...v)) : String(Math.min(...v))) : '—' }
    case 'max': { const v = numVals(); return v.length ? (isNum ? fmtMontant(Math.max(...v)) : String(Math.max(...v))) : '—' }
    case 'average': {
      const v = numVals(); if (!v.length) return '—'
      const avg = v.reduce((a, b) => a + b, 0) / v.length
      return isNum ? fmtMontant(avg) : avg.toLocaleString('fr-FR', { maximumFractionDigits: 2 })
    }
    case 'range': {
      const v = numVals(); if (v.length < 2) return '—'
      const r = Math.max(...v) - Math.min(...v)
      return isNum ? fmtMontant(r) : String(r)
    }
    case 'first': { const v = rows.length ? rows[0].getValue(colCode) : null; return v == null ? '—' : fmtSummaryValue(colType, v) }
    case 'last':  { const v = rows.length ? rows[rows.length - 1].getValue(colCode) : null; return v == null ? '—' : fmtSummaryValue(colType, v) }
    default: return ''
  }
}

// Aplatit récursivement l'arbre de colonnes (groupes via `children`) en ne gardant que les
// feuilles, SANS filtrage de visibilité — dans l'ordre de profondeur. Une colonne masquée
// (`visible: false`, sur elle-même ou un groupe ancêtre) reste une colonne à part entière pour le
// flux JSON d'alimentation (row) et l'API impérative (getColumnList, getColumnsCount, getData,
// getJson, addNode...) : seul son affichage (en-tête + cellules) est concerné, jamais sa
// présence/position dans les données. `columns[0]` (colonne d'arborescence) reste garantie être
// une feuille et traverse cette fonction inchangée en position 0.
function flattenLeafColumns(entries) {
  const result = []
  for (const entry of entries ?? []) {
    if (entry.children) result.push(...flattenLeafColumns(entry.children))
    else result.push(entry)
  }
  return result
}

// Variante réservée à la construction du modèle de colonnes TanStack (colDefs) : exclut
// uniquement les feuilles sous un groupe masqué (`visible: false` sur un ancêtre `children`) —
// jamais une feuille masquée elle-même, qui reste une colonne du modèle à part entière
// (redimensionnable, filtrable, triable...), simplement initialisée invisible (cf. l'état
// `columnVisibility`, initialisé plus bas) afin de rester listée — et réactivable — dans le
// panneau "Colonnes ▾". `columns[0]` traverse également cette fonction inchangée en position 0.
function flattenModelLeafColumns(entries) {
  const result = []
  for (const entry of entries ?? []) {
    if (entry.children) {
      if (entry.visible === false) continue
      result.push(...flattenModelLeafColumns(entry.children))
    } else {
      result.push(entry)
    }
  }
  return result
}

// Le bord droit d'un en-tête de groupe coïncide exactement avec le bord droit de sa dernière
// colonne feuille descendante (le colSpan ne fait que fusionner visuellement des cellules,
// aucune largeur ne lui est propre) — redimensionner le groupe revient donc à redimensionner
// cette feuille, sans logique de répartition à écrire.
function getRightmostLeafHeader(header) {
  let h = header
  while (h.subHeaders?.length > 0) h = h.subHeaders[h.subHeaders.length - 1]
  return h
}

function parseTreeNode(node, leafColumns) {
  const [nodeId, ...vals] = node.row
  const result = { id: nodeId, _isLeaf: node.isLeaf ?? false, _icon: node.icon ?? null }
  leafColumns.forEach((col, i) => { result[col.code] = normalizeCellValue(col, vals[i] ?? null) })
  if (node.children?.length) result.children = node.children.map(c => parseTreeNode(c, leafColumns))
  return result
}

// Sérialisation par type de colonne — cf. cellToJsonStr() (Select → "true"/"false", Date/DateTime
// → "JJ/MM/AAAA"[ HH24:mm:ss], autres types → toujours une chaîne ou null).
function serializeTreeNode(node, leafColumns, overridesByNodeId = {}) {
  const item = {
    row: [
      toJsonStr(node.id),
      ...leafColumns.map(c => cellToJsonStr(resolveEffectiveCol(overridesByNodeId, node.id, c), node[c.code])),
    ],
    isLeaf: node._isLeaf,
  }
  if (node._icon != null) item.icon = node._icon
  if (node.children?.length) item.children = node.children.map(c => serializeTreeNode(c, leafColumns, overridesByNodeId))
  return item
}

function flattenNodes(nodes) {
  const result = []
  const walk = list => { for (const n of list) { result.push(n); if (n.children?.length) walk(n.children) } }
  walk(nodes ?? [])
  return result
}

// Nœud parent direct de `nodeId` (recherche récursive parmi les `children`) — `null` si `nodeId`
// est un nœud racine ou introuvable (pas de distinction entre les deux cas, cohérent avec le
// contrat "getParentNode() rend le nœud parent ou null").
function findParentNode(nodes, nodeId) {
  for (const n of nodes ?? []) {
    if (n.children?.some(c => c.id === nodeId)) return n
    if (n.children?.length) {
      const found = findParentNode(n.children, nodeId)
      if (found) return found
    }
  }
  return null
}

// Supprime un nœud de l'arbre et retourne { newNodes, removed }
function removeNodeFromTree(nodes, nodeId) {
  let removed = null
  const filter = list => list.reduce((acc, n) => {
    if (n.id === nodeId) { removed = n; return acc }
    const children = n.children?.length ? filter(n.children) : n.children
    acc.push(children !== n.children ? { ...n, children } : n)
    return acc
  }, [])
  return { newNodes: filter(nodes), removed }
}

// Insère nodeToInsert relativement à refId selon position ('before'|'after'|'append')
function insertRelativeTo(nodes, refId, position, nodeToInsert) {
  return nodes.reduce((acc, n) => {
    if (position === 'append' && n.id === refId) {
      acc.push({ ...n, children: [...(n.children ?? []), nodeToInsert] })
      return acc
    }
    if ((position === 'before' || position === 'after') && n.id === refId) {
      if (position === 'before') { acc.push(nodeToInsert); acc.push(n) }
      else { acc.push(n); acc.push(nodeToInsert) }
      return acc
    }
    if (n.children?.length) acc.push({ ...n, children: insertRelativeTo(n.children, refId, position, nodeToInsert) })
    else acc.push(n)
    return acc
  }, [])
}

// Duplication profonde (nouveaux ids pour tous les nœuds)
function dupDeep(node) {
  const copy = { ...node, id: generateId() }
  if (node.children?.length) copy.children = node.children.map(dupDeep)
  return copy
}

// Duplication superficielle — conserve isLeaf mais sans enfants
function dupShallow(node) {
  const copy = { ...node, id: generateId() }
  delete copy.children
  if (!node._isLeaf) copy.children = []
  return copy
}

// Affichage d'une cellule de donnée (hors colonne d'arborescence `firstCol`) — extrait du dispatch
// par type autrefois inline dans `cell:` (buildLeafColumnDef), pour être réutilisable à la fois en
// lecture seule et dans EditableRenderedTreeCell (état "au repos" d'une colonne éditable avec
// renderer). `renderer`, si fourni, prime sur le dispatch par type (setColumnRenderer).
function TreeCellDisplay({ col, value, rowIndex, renderer }) {
  if (typeof renderer === 'function') {
    try { return renderer(value, rowIndex, col.code) }
    catch (e) { console.error(`[TreeField] Erreur dans setColumnRenderer("${col.code}")`, e); return fmtTreeCellValue(col.type, value) }
  }
  if (col.type === 'Select') return isSelectTrue(value) ? '✓' : ''
  if (col.type === 'Montant') return <span style={{ display: 'block', textAlign: 'right' }}>{fmtMontant(value)}</span>
  if (col.type === 'Numerique') return <span style={{ display: 'block', textAlign: 'right' }}>{value ?? ''}</span>
  if (col.type === 'Heure') return fmtTreeCellValue(col.type, value)
  if (col.type === 'Date' || col.type === 'DateTime') return fmtTreeCellValue(col.type, value)
  if (col.type === 'Password') return <PasswordCellDisplay value={value} />
  if (col.type === 'Combo') {
    const opt = (col.items ?? []).filter(item => !item[3]).find(([val]) => String(val) === String(value))
    return opt ? opt[1] : (value ?? '')
  }
  if (col.type === 'Link') {
    if (!value) return ''
    const linkStyle = { color: 'hsl(var(--primary))', display: 'inline-flex', alignItems: 'center' }
    if (String(value).startsWith('javascript:')) {
      const code = String(value).slice('javascript:'.length)
      return <a href="#" onClick={e => { e.preventDefault(); setTimeout(() => new Function(code)(), 0) }} style={linkStyle}><ExternalLink size={14} /></a>
    }
    return <a href={value} style={linkStyle}><ExternalLink size={14} /></a>
  }
  if (col.type === 'Code') return <code>{value ?? ''}</code>
  if (col.type === 'Image') return value ? <img src={value} alt="" style={{ maxHeight: 24 }} /> : ''
  if (col.type === 'HTML' || col.type === 'Texte') return <span dangerouslySetInnerHTML={{ __html: value ?? '' }} />
  if (col.type === 'File') return value instanceof File ? value.name : (value ?? '')
  return value ?? ''
}

// Colonne éditable (`mode: 'write'`) porteuse d'un `setColumnRenderer` — même raisonnement que
// EditableRenderedCell côté TableField.jsx : TreeCellEditor affiche toujours un input à l'état "au
// repos" pour les colonnes en écriture, donc un renderer prioritaire sur tout le cycle de vie de
// la cellule empêcherait définitivement l'édition du nœud. Rendu via `renderer` (TreeCellDisplay)
// au repos, bascule vers le véritable éditeur de colonne au clic, retour à l'affichage rendu une
// fois l'édition terminée (perte de focus).
function EditableRenderedTreeCell({ col, value, rowIndex, nodeId, onEvent, treeId, onCellChange, renderer }) {
  const [editing, setEditing] = useState(false)
  const wrapperRef = useRef(null)
  useEffect(() => {
    if (editing) wrapperRef.current?.querySelector('input, select, textarea')?.focus()
  }, [editing])
  if (!editing) {
    return (
      <div onClick={() => setEditing(true)} style={{ cursor: 'text', minHeight: '100%' }}>
        <TreeCellDisplay col={col} value={value} rowIndex={rowIndex} renderer={renderer} />
      </div>
    )
  }
  return (
    <div ref={wrapperRef} onBlur={() => setEditing(false)}>
      <TreeCellEditor col={col} value={value} nodeId={nodeId} rowIndex={rowIndex} onEvent={onEvent} treeId={treeId} onCellChange={onCellChange} />
    </div>
  )
}

// Combo dynamique — `setCellEditor(colId, { type: 'Combo', proxyLoader, callback })`, même principe
// que côté TableField.jsx (voir commentaire sur DynamicComboCellEditor) : `proxyLoader` au format
// ComboField (type 4), `callback` = nom de fonction résolue via `window[callback]`, appelée avec
// `(typeLoading, dynafilter, compoName, lineNo, columnCode, token)`. Si absents → `<select>`
// statique habituel (voir TreeCellEditor, branche `Combo`).
const DYNAMIC_COMBO_MIN_CHARS = 3
function DynamicComboTreeCellEditor({ col, value, rowIndex, nodeId, treeId, onEvent, onCellChange }) {
  const t = useT()
  const [token, setToken]     = useState(value != null ? String(value) : '')
  const [prevValue, setPrevValue] = useState(value)
  const [options, setOptions] = useState([])
  const [open, setOpen]       = useState(false)
  const [anchorRect, setAnchorRect] = useState(null)
  const debounceRef = useRef(null)
  const wrapperRef  = useRef(null)
  const dropdownRef = useRef(null)

  // Resynchronise `token` sur `value` quand ce dernier change en dehors de cet éditeur (proxy
  // set()/setCellValue()...) — même raisonnement que TreeCellEditor.
  if (prevValue !== value) {
    setPrevValue(value)
    setToken(value != null ? String(value) : '')
  }

  // Portail vers document.body (position: fixed, ancrée sur le rect de l'input) — voir
  // DynamicComboCellEditor (TableField.jsx) pour le détail du raisonnement (corps de l'arbre
  // scrollable, overflow-y: auto, rognerait un dropdown en absolute classique).
  useEffect(() => {
    if (!open) return
    const handler = e => {
      if (wrapperRef.current?.contains(e.target)) return
      if (dropdownRef.current?.contains(e.target)) return
      setOpen(false)
    }
    // Exclut le scroll interne du dropdown lui-même (`overflow-y: auto`) : sinon, faire défiler la
    // liste de résultats la referme aussitôt.
    const closeOnScroll = e => { if (!dropdownRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('scroll', closeOnScroll, true)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('scroll', closeOnScroll, true)
    }
  }, [open])

  const handleSearch = e => {
    const t = e.target.value
    setToken(t)
    if (!t || t.length < DYNAMIC_COMBO_MIN_CHARS) { setOptions([]); setOpen(false); return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      const fn = window[col.callback]
      if (typeof fn !== 'function') {
        console.error(`[TreeField] setCellEditor: callback "${col.callback}" introuvable sur window`)
        return
      }
      try {
        const items = await fn(col.proxyLoader?.typeLoading, col.proxyLoader?.dynafilter, treeId, rowIndex, col.code, t)
        setOptions(Array.isArray(items) ? items : [])
        setAnchorRect(wrapperRef.current?.getBoundingClientRect() ?? null)
        setOpen(true)
      } catch (e) {
        console.error(`[TreeField] Erreur dans le callback de setCellEditor("${col.code}")`, e)
        setOptions([])
        setOpen(false)
      }
    }, 300)
  }

  const selectOption = ([code, label]) => {
    setToken(label)
    // `prevValue` alignée dès maintenant sur `code` — voir DynamicComboCellEditor (TableField.jsx)
    // pour le détail : sans ça, la resynchronisation `token`/`value` écraserait le libellé qu'on
    // vient de poser par le code brut au prochain rendu (`value` stocké est le code, pas le libellé).
    setPrevValue(code)
    setOptions([])
    setOpen(false)
    onCellChange?.current?.(nodeId, col.code, code)
    // Re-sélection du même item (valeur inchangée) : pas d'onTreeGridRowEdit redondant.
    if (cellValuesEqual(col, code, value)) return
    onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code)
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <Search size={12} style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', color: '#8a94a6', pointerEvents: 'none' }} />
      <input type="text" value={token} placeholder={col.placeholder}
        onChange={handleSearch}
        autoComplete="off"
        style={{ paddingLeft: 20 }}
        className={styles.cellInput} />
      {open && anchorRect && createPortal(
        <div
          ref={dropdownRef}
          className={styles.thFilterDropdown}
          style={{ position: 'fixed', left: anchorRect.left, top: anchorRect.bottom, width: anchorRect.width, zIndex: OVERLAY_Z_INDEX }}
        >
          {options.filter(item => !item[3]).length > 0
            ? options.filter(item => !item[3]).map(([code, label], i) => (
                <div key={i} className={styles.thFilterOption} onMouseDown={() => selectOption([code, label])}>{label}</div>
              ))
            : <div className={styles.thFilterEmpty}>{t('noResult')}</div>
          }
        </div>,
        document.body
      )}
    </div>
  )
}

function TreeCellEditor({ col, value, nodeId, rowIndex, onEvent, treeId, onCellChange }) {
  const [val, setVal] = useState(value ?? '')
  const [prevValue, setPrevValue] = useState(value)
  const [showPwd, setShowPwd] = useState(false)
  const valRef = useRef(val)
  useLayoutEffect(() => { valRef.current = val })
  const dateInputRef = useRef(null)
  // showPicker() : API standard (Chrome/Edge 99+, Firefox 101+) pour ouvrir le picker natif depuis
  // un déclencheur quelconque — nécessaire ici puisque sous Chrome l'icône native est masquée (CSS
  // `.tbs-date-input`) et remplacée par ce bouton (col.type === 'Date' plus bas). try/catch :
  // showPicker() lève (ex. NotAllowedError) si le champ est désactivé ou hors du DOM au moment du
  // clic — fallback focus() plutôt que de laisser l'exception remonter.
  const openDatePicker = () => {
    try { dateInputRef.current?.showPicker?.() }
    catch { dateInputRef.current?.focus() }
  }
  // Resynchronise `val` sur `value` quand ce dernier change en dehors de cet éditeur (proxy
  // set()/setCellValue()...) — même raisonnement que CellEditor (TableField.jsx).
  if (prevValue !== value) {
    setPrevValue(value)
    setVal(value ?? '')
  }

  const fireEdit = useCallback(() => {
    onCellChange?.current?.(nodeId, col.code, valRef.current)
    // Valeur inchangée (ex. focus + blur sans frappe) : pas d'onTreeGridRowEdit redondant.
    if (cellValuesEqual(col, normalizeCellValue(col, valRef.current), value)) return
    onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code)
  }, [nodeId, col, value, rowIndex, onEvent, treeId, onCellChange])
  const clearVal = () => { setVal(''); valRef.current = ''; onCellChange?.current?.(nodeId, col.code, ''); onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code) }

  if (col.type === 'Select')
    return <input type="checkbox" checked={isSelectTrue(value)} onChange={e => {
      const v = String(e.target.checked)
      setVal(v); valRef.current = v
      onCellChange?.current?.(nodeId, col.code, v)
      onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code)
    }} />
  if (col.type === 'Combo' && col.proxyLoader && col.callback)
    return <DynamicComboTreeCellEditor col={col} value={value} rowIndex={rowIndex} nodeId={nodeId} treeId={treeId} onEvent={onEvent} onCellChange={onCellChange} />
  if (col.type === 'Combo')
    return (
      <select value={val ?? ''} onChange={e => {
        const v = e.target.value
        setVal(v); valRef.current = v
        onCellChange?.current?.(nodeId, col.code, v)
        onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code)
      }} className={styles.cellSelect}>
        <option value="">—</option>
        {buildColGroups(col.items ?? []).map((g, gi) =>
          g.groupLabel != null
            ? (
              <optgroup key={gi} label={g.groupLabel}>
                {g.items.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
              </optgroup>
            ) : (
              g.items.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)
            )
        )}
      </select>
    )
  if (col.type === 'Password')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type={showPwd ? 'text' : 'password'} value={val}
          autoComplete="new-password"
          onChange={e => setVal(e.target.value)} onBlur={fireEdit}
          maxLength={col.maxSize ?? undefined}
          style={val ? { paddingRight: 32 } : { paddingRight: 18 }}
          className={styles.cellInput} />
        <button type="button" tabIndex={-1} onClick={() => setShowPwd(v => !v)}
          style={{ ...CELL_CLEAR_BTN, right: val ? 18 : 2 }}>
          {showPwd ? <EyeOff size={10} /> : <Eye size={10} />}
        </button>
        {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'Montant' || col.type === 'Numerique')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type="number" value={val}
          onChange={e => setVal(e.target.value)} onBlur={fireEdit}
          style={String(val) !== '' ? { paddingRight: 20 } : undefined}
          className={`${styles.cellInput} ${styles.cellInputRight}`} />
        {String(val) !== '' && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'Date')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type="date" ref={dateInputRef} value={dateToIsoInput(val)}
          onChange={e => setVal(isoInputToDate(e.target.value))} onBlur={fireEdit}
          // Chrome/Edge/Safari (HAS_WEBKIT_DATE_ICON) : icône native masquée (CSS `.tbs-date-input`)
          // et remplacée par le bouton calendrier ci-dessous, toujours réservé (paddingRight 18/32,
          // mêmes paliers que le double bouton œil+croix de 'Password' plus haut dans ce fichier —
          // espacement déjà éprouvé pour deux icônes superposées sans chevauchement). Firefox : son
          // icône native (non masquable) se positionne elle-même contre `padding-right` (vérifié
          // par test) — 15px quand la croix de réinitialisation est affichée pour les rapprocher,
          // comme ChampField.jsx (mc=104).
          style={HAS_WEBKIT_DATE_ICON ? { paddingRight: val ? 32 : 18 } : (val ? { paddingRight: 15 } : undefined)}
          className={`${styles.cellInput} tbs-date-input`} />
        {HAS_WEBKIT_DATE_ICON && (
          <button type="button" tabIndex={-1} onClick={openDatePicker} style={{ ...CELL_CLEAR_BTN, right: val ? 18 : 2 }}>
            <Calendar size={10} />
          </button>
        )}
        {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'DateTime')
    return (
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input type="datetime-local" ref={dateInputRef} value={dateTimeToIsoInput(val)}
          onChange={e => setVal(isoInputToDateTime(e.target.value))} onBlur={fireEdit}
          // Même motif que 'Date' ci-dessus (icône native masquée/remplacée sous Chrome, padding
          // Firefox 15px) — un `<input type="datetime-local">` porte la même icône native
          // `::-webkit-calendar-picker-indicator`, avec le même seuil de largeur en dessous duquel
          // Chrome la masque (d'autant plus probable ici : contenu plus large, jour+mois+année+heure).
          style={HAS_WEBKIT_DATE_ICON ? { paddingRight: val ? 32 : 18 } : (val ? { paddingRight: 15 } : undefined)}
          className={`${styles.cellInput} tbs-date-input`} />
        {HAS_WEBKIT_DATE_ICON && (
          <button type="button" tabIndex={-1} onClick={openDatePicker} style={{ ...CELL_CLEAR_BTN, right: val ? 18 : 2 }}>
            <Calendar size={10} />
          </button>
        )}
        {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
      </div>
    )
  if (col.type === 'Heure')
    return (
      <select value={val ?? ''} onChange={e => {
        const v = e.target.value === '' ? null : Number(e.target.value)
        setVal(v); valRef.current = v
        onCellChange?.current?.(nodeId, col.code, v)
        onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code)
      }} className={styles.cellSelect}>
        <option value="">—</option>
        {Array.from({ length: 25 }, (_, h) => <option key={h} value={h}>{h}h</option>)}
      </select>
    )
  if (col.type === 'File')
    return (
      <input type="file" onChange={e => {
        const file = e.target.files?.[0] ?? null
        onCellChange?.current?.(nodeId, col.code, file)
        onEvent(treeId, 'onTreeGridRowEdit', rowIndex, col.code)
      }} className={styles.cellInput} />
    )
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <input type="text" value={val} maxLength={col.maxSize ?? undefined}
        onChange={e => setVal(e.target.value)} onBlur={fireEdit}
        style={val ? { paddingRight: 20 } : undefined}
        className={styles.cellInput} />
      {val && <button type="button" tabIndex={-1} onClick={clearVal} style={CELL_CLEAR_BTN}><X size={10} /></button>}
    </div>
  )
}

// React.memo — voir TableField.jsx pour le détail (mêmes 3 volets forceMount d'un TabsField).
function TreeField({
  id, label, icon, columns = [],
  sortColumn, sortDir,
  collapseGrouping,
  collapsible = false, collapsedOnLoad = false,
  rowNumber = false, fitColumns = false,
  toolbarBtns = [], dragDropId, fixedHeight, resizable, visible,
  onEvent, compoGroup, onDataLoaded, userData,
  treeLoader, treeDragDropCallback,
  class: cssClass, style: rawStyle,
}) {
  const t = useT()
  const cssStyle = toStyleObject(rawStyle)
  // `visible: false` (JSON) → display:none posé directement sur le div [data-tbs-root] : voir
  // ChampField.jsx pour le détail (show()/hide() de tbsGeneralApi.js ciblent ce même élément).
  const visibilityStyle = visible === false ? { display: 'none' } : undefined
  // ── Chargement inliné (remplace useTableData) ──────────────────────
  const [loadTick, setLoadTick]     = useState(0)
  const extraParamsRef              = useRef(undefined)
  const [fetchedData, setFetchedData] = useState([])
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    let result
    try {
      result = (treeLoader || window.onTbsTreeLoader)(id, extraParamsRef.current)
    } catch (e) {
      console.error(`[TreeField ${id}] Échec du chargement (onTbsTreeLoader) :`, e)
      setError(e.message ?? String(e))
      setLoading(false)
      return
    }
    Promise.resolve(result)
      .then(items => {
        // Contrat documenté (CLAUDE.md) : onTbsTreeLoader doit résoudre directement le tableau de
        // nœuds — c'est ce que fait le mock (`data.items ?? []`, extraction faite avant résolution).
        // Un treeLoader métier qui résout l'objet brut `{ items: [...] }` sans l'extraire (forme
        // pourtant très répandue côté API) ne lève aucune exception synchrone ni rejet de promesse
        // ici — seul un `.map()` plus loin plantait jusqu'ici (TypeError non rattrapée, écran
        // cassé). Tolère donc aussi cette forme, par robustesse (même esprit que le try/catch
        // ci-dessus) ; toute autre forme passe en erreur plutôt que de laisser planter le rendu.
        const arr = Array.isArray(items) ? items : (Array.isArray(items?.items) ? items.items : null)
        if (arr === null) {
          console.error(`[TreeField ${id}] onTbsTreeLoader a résolu une valeur inattendue (ni tableau, ni { items: [...] }) :`, items)
          setError('Données chargées invalides')
          setFetchedData([])
        } else {
          setFetchedData(arr)
        }
        setLoading(false)
      })
      .catch(e => {
        console.error(`[TreeField ${id}] Échec du chargement (onTbsTreeLoader) :`, e)
        setError(e.message ?? String(e))
        setLoading(false)
      })
  }, [id, loadTick, treeLoader])

  const [rows, setRows]               = useState([])
  const [selectedId, setSelectedId]   = useState(null)
  // Survol de ligne — même raison que TableField.jsx (stickyTd ci-dessous) : les cellules des
  // colonnes figées (`lock`) posent leur fond en style inline, qui prime sur la couleur de survol
  // CSS (`.tr:hover .td`/`.trLeaf:hover .td`, fields.module.css) et la masquerait sinon sur ces
  // colonnes.
  const [hoveredId, setHoveredId] = useState(null)
  const [collapsed, setCollapsed]     = useState(collapsedOnLoad)
  const [title, setTitle]             = useState(label)
  const [dragOverRowId, setDragOverRowId] = useState(null)
  const [dragOverPos, setDragOverPos]     = useState(null)
  const [toolbarState, setToolbarState]   = useState(toolbarBtns)
  const [toolbarVisible, setToolbarVisible] = useState(true)
  const [treeError, setTreeError]     = useState(null)
  const [columnRenderers, setColumnRenderers] = useState({})
  // Overrides runtime par colonne (clé = code) — pour l'instant uniquement `items`, posé par
  // setComboEditorItems() sur le proxy colonne (getColumnList()). Même mécanisme que colOverrides
  // dans TableField.jsx (absent ici jusqu'ici, TreeField n'exposant jusqu'à présent aucun setter
  // par colonne mutable) — fusionné dans modelCols, voir plus bas.
  const [colOverrides, setColOverrides] = useState({})
  // Overrides d'éditeur par CELLULE (nodeId + code colonne) — setCellEditor() sur le proxy de nœud.
  // Distinct de colOverrides (par colonne, tous les nœuds) : ici seul le nœud visé est affecté.
  // Même mécanisme que TableField.jsx (cellEditorOverrides). { [nodeId]: { [colCode]: {type, maxSize, placeholder, items} } }
  const [cellEditorOverrides, setCellEditorOverrides] = useState({})
  // Classes CSS additionnelles par nœud (addClassCss/removeClassCss sur le proxy de nœud).
  // { [nodeId]: string[] }
  const [rowClassOverrides, setRowClassOverrides] = useState({})
  const [, setRefreshTick] = useState(0)

  const rowsRef             = useRef(rows)
  const selectedIdRef       = useRef(null)
  const compoGroupRef       = useRef(compoGroup ?? null)
  const userDataRef         = useRef(userData ?? null)
  const onTreeCellChangeRef = useRef(null)
  const columnsRef          = useRef(columns)
  // Colonnes feuilles aplaties (hors colonnes de groupement) — c'est sur cette liste que
  // s'appuient le flux JSON d'alimentation et toute l'API impérative par colonne (getColumnList,
  // getColumnsCount, getData, addNode...), jamais sur les colonnes de groupement.
  const leafColumnsRef      = useRef(flattenLeafColumns(columns))
  const colOverridesRef     = useRef({})
  const columnVisibilityRef = useRef({})
  const cellEditorOverridesRef = useRef({})
  const rowClassOverridesRef = useRef({})
  const toolbarStateRef     = useRef(toolbarBtns)
  useLayoutEffect(() => {
    rowsRef.current = rows
    selectedIdRef.current = selectedId
    columnsRef.current = columns
    leafColumnsRef.current = flattenLeafColumns(columns)
    colOverridesRef.current = colOverrides
    columnVisibilityRef.current = columnVisibility
    cellEditorOverridesRef.current = cellEditorOverrides
    rowClassOverridesRef.current = rowClassOverrides
    toolbarStateRef.current = toolbarState
    onTreeCellChangeRef.current = (nId, colCode, value) => {
      const col = leafColumnsRef.current.find(c => c.code === colCode)
      const update = nodes => nodes.map(n =>
        n.id === nId ? { ...n, [colCode]: normalizeCellValue(col, value) }
        : n.children?.length ? { ...n, children: update(n.children) }
        : n
      )
      setRowsSynced(prev => update(prev))
    }
  })

  // rowsRef.current n'est autrement resynchronisé qu'après le rendu suivant (useLayoutEffect
  // ci-dessus) : un appel synchrone à getData()/getSelectedNode()/getJson() juste après un
  // setRows() — par ex. depuis un handler onLoadTreeGrid/onLoad métier appelé synchrone à la fin
  // du chargement — lirait alors encore l'ancien tableau (souvent vide), avant que React n'ait
  // eu l'occasion de commit + exécuter l'effet. setRowsSynced tient rowsRef à jour immédiatement.
  const setRowsSynced = useCallback((updaterOrValue) => {
    // La fonction passée à setRows() n'est PAS garantie de s'exécuter de façon synchrone (React
    // peut la reporter à son prochain rendu) — on calcule donc `next` nous-mêmes, à partir de
    // rowsRef.current (toujours à jour via ce même mécanisme), puis on assigne la ref en premier.
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(rowsRef.current) : updaterOrValue
    rowsRef.current = next
    setRows(next)
  }, [])

  // Même schéma, pour colOverrides — lu par validate() (potentiellement) et par setComboEditorItems()
  // via colOverridesRef. Sans lui, un setComboEditorItems() suivi d'un getColumnList() synchrone
  // verrait encore l'ancien override (cas déjà rencontré côté TableField, colOverrides).
  const setColOverridesSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(colOverridesRef.current) : updaterOrValue
    colOverridesRef.current = next
    setColOverrides(next)
  }, [])

  // Même schéma, pour cellEditorOverrides — lu par le cell renderer, les proxies de nœud,
  // getJson() et validate() via cellEditorOverridesRef.
  const setCellEditorOverridesSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(cellEditorOverridesRef.current) : updaterOrValue
    cellEditorOverridesRef.current = next
    setCellEditorOverrides(next)
  }, [])

  // Même schéma, pour rowClassOverrides — classes CSS additionnelles posées par nœud via
  // addClassCss()/removeClassCss() sur le proxy de nœud (makeNodeProxyById/makeNodeProxy).
  const setRowClassOverridesSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(rowClassOverridesRef.current) : updaterOrValue
    rowClassOverridesRef.current = next
    setRowClassOverrides(next)
  }, [])

  // Ajoute/retire une ou plusieurs classes CSS (séparées par des espaces) sur un nœud précis
  // (nodeId) — addClassCss()/removeClassCss() du proxy de nœud. Pas d'effet si nodeId absent
  // (nœud déjà supprimé) ou si `names` ne contient aucune classe exploitable.
  const addRowClassCss = (nodeId, names) => {
    if (nodeId == null) return
    const toAdd = String(names).split(' ').filter(Boolean)
    if (!toAdd.length) return
    setRowClassOverridesSynced(o => {
      const existing = new Set(o[nodeId] ?? [])
      toAdd.forEach(c => existing.add(c))
      return { ...o, [nodeId]: [...existing] }
    })
  }
  const removeRowClassCss = (nodeId, names) => {
    if (nodeId == null) return
    const toRemove = new Set(String(names).split(' ').filter(Boolean))
    if (!toRemove.size) return
    setRowClassOverridesSynced(o => {
      if (!o[nodeId]) return o
      const next = o[nodeId].filter(c => !toRemove.has(c))
      if (next.length === o[nodeId].length) return o
      // eslint-disable-next-line no-unused-vars
      const { [nodeId]: _removed, ...rest } = o
      return next.length ? { ...rest, [nodeId]: next } : rest
    })
  }

  // Même schéma, pour toolbarState — lu par getListOfToolbarBtns() via toolbarStateRef. Sans lui,
  // ce getter lirait la valeur figée au montage (l'effet qui expose window.tbsComponents[id] ne
  // s'exécute qu'une fois, deps [id]) : addElemToolbar()/removeElemToolbar()/... n'y apparaîtraient
  // alors jamais.
  const setToolbarStateSynced = useCallback((updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(toolbarStateRef.current) : updaterOrValue
    toolbarStateRef.current = next
    setToolbarState(next)
  }, [])

  // Sélectionne un nœud en gardant selectedIdRef synchrone : lu immédiatement par
  // getSelectedNode()/getSelectedNodeIndex() pendant le traitement synchrone des events déclenchés
  // juste après (onTreeGridSelRow, onTreeCellClicked, onTreeGridRowEdit...) — un state React ne
  // serait répercuté qu'au rendu suivant, donc lu comme "l'ancienne" sélection sinon.
  const selectNode = useCallback(nodeId => {
    selectedIdRef.current = nodeId
    setSelectedId(nodeId)
  }, [])

  // Fait défiler jusqu'à un nœud identifié par son id (voir data-tbs-row-id posé sur chaque <tr>)
  // — utilisé par addNode() : un tri actif peut placer le nœud ajouté n'importe où dans la liste
  // triée de ses frères, sans rapport avec l'ordre d'insertion. Différé via requestAnimationFrame :
  // le nœud doit d'abord avoir été effectivement rendu (commit React suivant setRowsSynced/expand)
  // avant de pouvoir le localiser dans le DOM.
  // Ajuste directement `scrollTop`/`scrollLeft` du conteneur interne (`.tableScroll`) plutôt qu'un
  // `Element.scrollIntoView()` sur le nœud lui-même — voir TableField.jsx pour le détail du
  // raisonnement (celui-ci ferait aussi défiler un canvas englobant ou la page).
  // Double rAF : une seule frame ne suffit pas toujours à garantir que le commit React du nouveau
  // nœud a été peint (observé notamment quand l'arbre est imbriqué dans un canvas lui-même
  // scrollable) — le nœud n'existe alors pas encore dans le DOM au moment du calcul.
  const scrollRowIntoView = useCallback(nodeId => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const rowEl = document.querySelector(`[data-tbs-root="${id}"] [data-tbs-row-id="${nodeId}"]`)
        const container = rowEl?.closest(`.${styles.tableScroll}`)
        if (!rowEl || !container) return
        const rowRect = rowEl.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        if (rowRect.top < containerRect.top) {
          container.scrollTop -= containerRect.top - rowRect.top
        } else if (rowRect.bottom > containerRect.bottom) {
          container.scrollTop += rowRect.bottom - containerRect.bottom
        }
        if (rowRect.left < containerRect.left) {
          container.scrollLeft -= containerRect.left - rowRect.left
        } else if (rowRect.right > containerRect.right) {
          container.scrollLeft += rowRect.right - containerRect.right
        }
      })
    })
  }, [id])

  useEffect(() => {
    window.tbsComponents = window.tbsComponents ?? {}

    // setCellEditor(colId, json) : json = {type, value?, maxSize?, placeholder?, items?, proxyLoader?,
    // callback?} — pose un éditeur propre à CE nœud (prioritaire, pour l'édition et get()/getJson(),
    // sur l'éditeur par défaut de la colonne), jusqu'à retrait explicite (json == null). Même contrat
    // que TableField.jsx. `proxyLoader`/`callback` : combo dynamique (type Combo) — voir
    // DynamicComboTreeCellEditor.
    const makeSetCellEditor = nodeId => (colId, json) => {
      const col = leafColumnsRef.current.find(c => c.code === colId)
      if (!col) throw new Error(`setCellEditor: colonne "${colId}" introuvable`)
      if (json == null) {
        setCellEditorOverridesSynced(o => {
          if (!o[nodeId]) return o
          // eslint-disable-next-line no-unused-vars
          const { [colId]: _removed, ...restCols } = o[nodeId]
          // eslint-disable-next-line no-unused-vars
          const { [nodeId]: _removedNode, ...rest } = o
          return Object.keys(restCols).length ? { ...rest, [nodeId]: restCols } : rest
        })
        return
      }
      const { type, value, maxSize, placeholder, items, proxyLoader, callback } = json
      setCellEditorOverridesSynced(o => ({
        ...o,
        [nodeId]: { ...(o[nodeId] ?? {}), [colId]: { type, maxSize, placeholder, items, proxyLoader, callback } },
      }))
      if (value !== undefined) {
        const effectiveCol = type != null ? { ...col, type } : col
        const update = nodes => nodes.map(n =>
          n.id === nodeId ? { ...n, [colId]: normalizeCellValue(effectiveCol, value) }
          : n.children?.length ? { ...n, children: update(n.children) }
          : n
        )
        setRowsSynced(prev => update(prev))
      }
    }

    // Proxy d'un nœud identifié par son id (stable après setRows)
    const makeNodeProxyById = (nodeId) => ({
      get:         colId => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, nodeId, col)
        return normalizeCellValue(effectiveCol, flattenNodes(rowsRef.current).find(n => n.id === nodeId)?.[colId] ?? null)
      },
      set:         (colId, val) => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, nodeId, col)
        const update = nodes => nodes.map(n =>
          n.id === nodeId ? { ...n, [colId]: normalizeCellValue(effectiveCol, val) }
          : n.children?.length ? { ...n, children: update(n.children) }
          : n
        )
        setRowsSynced(prev => update(prev))
      },
      getData:     () => {
        const node = flattenNodes(rowsRef.current).find(n => n.id === nodeId)
        return Object.fromEntries(leafColumnsRef.current.map(col => [col.code, node?.[col.code] ?? null]))
      },
      getId:       () => nodeId,
      isLeaf:      () => !!flattenNodes(rowsRef.current).find(n => n.id === nodeId)?._isLeaf,
      getParentNode: () => {
        const parent = findParentNode(rowsRef.current, nodeId)
        return parent ? makeNodeProxyById(parent.id) : null
      },
      getNodeIndex: () => flattenNodes(rowsRef.current).findIndex(n => n.id === nodeId),
      getChildren: () => {
        const node = flattenNodes(rowsRef.current).find(n => n.id === nodeId)
        return (node?.children ?? []).map(c => makeNodeProxyById(c.id))
      },
      getTree:     () => window.tbsComponents[id],
      setCellEditor: makeSetCellEditor(nodeId),
      addClassCss:    names => addRowClassCss(nodeId, names),
      removeClassCss: names => removeRowClassCss(nodeId, names),
    })
    // Proxy d'un nœud par index depth-first
    const makeNodeProxy = rowNum => {
      const node = flattenNodes(rowsRef.current)[rowNum]
      if (!node) return null
      return makeNodeProxyById(node.id)
    }

    window.tbsComponents[id] = {
      type: 'tree',
      ...createGeneralApi(id, userDataRef.current),
      // Cf. commentaire sur scrollElRef plus bas — cible la zone de défilement, pas [data-tbs-root].
      setHeight: height => { const el = scrollElRef.current; if (el) el.style.height = toCssLength(height) },
      getHeight: ()      => scrollElRef.current ? scrollElRef.current.offsetHeight : undefined,
      getData:  () => rowsRef.current.map(n => serializeTreeNode(n, leafColumnsRef.current, cellEditorOverridesRef.current)),
      setData:  items => setRowsSynced((items ?? []).map(n => parseTreeNode(n, leafColumnsRef.current))),
      getJson:  () => ({ items: rowsRef.current.map(n => serializeTreeNode(n, leafColumnsRef.current, cellEditorOverridesRef.current)) }),
      getCompoGroup: () => compoGroupRef.current,
      setCompoGroup: v => { compoGroupRef.current = v },
      setTitle:      t => setTitle(t),

      // Toolbar
      showToolbar:        ()                            => setToolbarVisible(true),
      hideToolbar:        ()                            => setToolbarVisible(false),
      addElemToolbar:     (btnId, icon, tip, vis, act) => setToolbarStateSynced(s => [...s, { id: btnId, icon, tooltip: tip, visible: vis, active: act }]),
      removeElemToolbar:  btnId                         => setToolbarStateSynced(s => s.filter(b => b.id !== btnId)),
      disableElemToolbar: btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: false } : b)),
      enableElemToolbar:  btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, active: true }  : b)),
      showElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: true }  : b)),
      hideElemToolbar:    btnId                         => setToolbarStateSynced(s => s.map(b => b.id === btnId ? { ...b, visible: false } : b)),
      getListOfToolbarBtns: () => toolbarStateRef.current.map(b => b.id),

      // Expand / collapse — expanded (TanStack) est indexé par row.id, égal à l'id du nœud
      // (cf. getRowId dans useReactTable ci-dessous)
      collapseAll: () => setExpanded({}),
      expandAll:   () => setExpanded(true),
      collapse: nodeId => setExpanded(prev => {
        if (prev === true) {
          const ids = flattenNodes(rowsRef.current).filter(n => n.children?.length).map(n => n.id)
          const map = Object.fromEntries(ids.map(i => [i, true]))
          delete map[nodeId]
          return map
        }
        const rest = { ...prev }
        delete rest[nodeId]
        return rest
      }),
      expand: nodeId => setExpanded(prev => {
        if (prev === true) return prev
        const node = flattenNodes(rowsRef.current).find(n => n.id === nodeId)
        if (!node) return prev
        const collectIds = n => n.children?.length ? [n.id, ...n.children.flatMap(collectIds)] : []
        const ids = collectIds(node)
        if (!ids.length) return prev
        return { ...prev, ...Object.fromEntries(ids.map(i => [i, true])) }
      }),

      // Nœuds — rowNum = index dans le parcours depth-first sur TOUS les nœuds
      getNbNodes:           () => flattenNodes(rowsRef.current).length,
      getSelectedNodeIndex: () => {
        const selId = selectedIdRef.current
        if (!selId) return -1
        return flattenNodes(rowsRef.current).findIndex(n => n.id === selId)
      },
      setSelectedNode: rowNum => {
        const node = flattenNodes(rowsRef.current)[rowNum]
        if (node) selectNode(node.id)
      },
      getSelectedNode: () => {
        const selId = selectedIdRef.current
        if (!selId) return null
        const idx = flattenNodes(rowsRef.current).findIndex(n => n.id === selId)
        return idx < 0 ? null : makeNodeProxy(idx)
      },
      getNodeAt: rowNum => makeNodeProxy(rowNum),
      getNodeById: nodeId => {
        const exists = flattenNodes(rowsRef.current).find(n => n.id === nodeId)
        return exists ? makeNodeProxyById(nodeId) : null
      },
      getCellValue: (rowNum, colId) => {
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const nodeId = flattenNodes(rowsRef.current)[rowNum]?.id
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, nodeId, col)
        return normalizeCellValue(effectiveCol, flattenNodes(rowsRef.current)[rowNum]?.[colId] ?? null)
      },
      setCellValue: (rowNum, colId, val) => {
        const targetId = flattenNodes(rowsRef.current)[rowNum]?.id
        if (targetId == null) return
        const col = leafColumnsRef.current.find(c => c.code === colId)
        const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, targetId, col)
        const update = nodes => nodes.map(n =>
          n.id === targetId ? { ...n, [colId]: normalizeCellValue(effectiveCol, val) }
          : n.children?.length ? { ...n, children: update(n.children) }
          : n
        )
        setRowsSynced(prev => update(prev))
      },

      // Ajoute un nœud dans parentNodeId (null = racine)
      addNode: (parentNodeId, line, icon, isLeaf, fireEvent) => {
        const { id: nodeId, ...colVals } = line ?? {}
        if (!nodeId) throw new Error('addNode: l\'attribut id est obligatoire dans line')
        if (parentNodeId !== null) {
          const parent = flattenNodes(rowsRef.current).find(n => n.id === parentNodeId)
          if (parent?._isLeaf) throw new Error(`addNode: le nœud "${parentNodeId}" est une feuille`)
        }
        const newNode = { id: nodeId, _isLeaf: !!isLeaf, _icon: icon ?? null }
        leafColumnsRef.current.forEach(col => { newNode[col.code] = normalizeCellValue(col, colVals[col.code] ?? null) })
        if (!isLeaf) newNode.children = []
        const insertInto = nodes => {
          if (parentNodeId === null) return [...nodes, newNode]
          return nodes.map(n => {
            if (n.id === parentNodeId) return { ...n, children: [...(n.children ?? []), newNode] }
            if (n.children?.length) return { ...n, children: insertInto(n.children) }
            return n
          })
        }
        setRowsSynced(prev => insertInto(prev))
        if (fireEvent) onEvent(id, 'onRowAdd')
        // Sélectionne + fait défiler jusqu'au nœud ajouté — même raisonnement que
        // TableField.addRow() (un tri actif peut le placer n'importe où parmi ses frères). Le
        // parent direct est déplié au passage (un nœud replié n'est pas rendu dans le DOM, donc pas
        // localisable pour le scroll) — pas un dépli en profondeur (voir expand() ci-dessus).
        if (parentNodeId !== null) setExpanded(prev => (prev === true ? prev : { ...prev, [parentNodeId]: true }))
        selectNode(nodeId)
        scrollRowIntoView(nodeId)
        return makeNodeProxyById(nodeId)
      },
      // Ajoute plusieurs nœuds dans parentNodeId (null = racine) — pendant de addRows() côté
      // 'table'. icon/isLeaf partagés par tous les nœuds ajoutés (structurels, pas des colonnes) ;
      // pour des nœuds hétérogènes (icônes/feuille différentes), appeler addNode() en boucle.
      addNodes: (parentNodeId, linesList, icon, isLeaf) => {
        if (parentNodeId !== null) {
          const parent = flattenNodes(rowsRef.current).find(n => n.id === parentNodeId)
          if (parent?._isLeaf) throw new Error(`addNodes: le nœud "${parentNodeId}" est une feuille`)
        }
        const newNodes = (linesList ?? []).map(line => {
          const { id: nodeId, ...colVals } = line ?? {}
          if (!nodeId) throw new Error('addNodes: l\'attribut id est obligatoire dans chaque line')
          const newNode = { id: nodeId, _isLeaf: !!isLeaf, _icon: icon ?? null }
          leafColumnsRef.current.forEach(col => { newNode[col.code] = normalizeCellValue(col, colVals[col.code] ?? null) })
          if (!isLeaf) newNode.children = []
          return newNode
        })
        const insertInto = nodes => {
          if (parentNodeId === null) return [...nodes, ...newNodes]
          return nodes.map(n => {
            if (n.id === parentNodeId) return { ...n, children: [...(n.children ?? []), ...newNodes] }
            if (n.children?.length) return { ...n, children: insertInto(n.children) }
            return n
          })
        }
        setRowsSynced(prev => insertInto(prev))
        newNodes.forEach(() => onEvent(id, 'onRowAdd'))
        // Sélectionne + révèle le premier des nœuds ajoutés — même raisonnement que addNode().
        if (newNodes.length > 0) {
          if (parentNodeId !== null) setExpanded(prev => (prev === true ? prev : { ...prev, [parentNodeId]: true }))
          selectNode(newNodes[0].id)
          scrollRowIntoView(newNodes[0].id)
        }
      },

      // Déplace un nœud relativement à nodeReferenceId ('before'|'after'|'append')
      moveNode: (nodeId, nodeReferenceId, position) => {
        setRowsSynced(prev => {
          const { newNodes, removed } = removeNodeFromTree(prev, nodeId)
          if (!removed) return prev
          return insertRelativeTo(newNodes, nodeReferenceId, position, removed)
        })
      },

      // Duplique un nœud — deeply: true → sous-nœuds inclus, false → nœud seul
      dupNode: (nodeId, nodeReferenceId, position, deeply, fireEvent) => {
        const srcNode = flattenNodes(rowsRef.current).find(n => n.id === nodeId)
        if (!srcNode) return null
        const newNode = deeply ? dupDeep(srcNode) : dupShallow(srcNode)
        setRowsSynced(prev => insertRelativeTo(prev, nodeReferenceId, position, newNode))
        if (fireEvent) onEvent(id, 'onRowAdd')
        return makeNodeProxyById(newNode.id)
      },

      // Supprime un nœud (et ses descendants) par id — émet onRowDelete(nodeId) seulement si fireEvent===true
      removeNode: (nodeId, fireEvent) => {
        const { newNodes, removed } = removeNodeFromTree(rowsRef.current, nodeId)
        if (!removed) return
        setRowsSynced(newNodes)
        const removedIds = new Set(flattenNodes([removed]).map(n => n.id))
        if (selectedIdRef.current != null && removedIds.has(selectedIdRef.current)) selectNode(null)
        if (fireEvent) onEvent(id, 'onRowDelete', nodeId)
      },

      emptySelection: (fireEvent) => {
        selectNode(null)
        if (fireEvent) onEvent(id, 'onTreeGridSelRow', -1, null)
      },
      getColumnsCount: () => leafColumnsRef.current.length + 1, // +1 pour l'id interne
      getColumnList: () => [
        {
          getCode: () => '_rowId',
          getLabel: () => null,
          getColumnIndex: () => 0,
          getType: () => null,
          isReadOnly: () => true,
          setComboEditorItems: () => { throw new Error('setComboEditorItems: la colonne "_rowId" n\'est pas de type Combo') },
          getComboEditorItems: () => { throw new Error('getComboEditorItems: la colonne "_rowId" n\'est pas de type Combo') },
        },
        ...leafColumnsRef.current.map((col, i) => ({
          getCode: () => col.code,
          getLabel: () => col.label ?? null,
          getColumnIndex: () => i + 1, // +1 : _rowId occupe l'indice 0
          getType: () => col.type,
          // Pas d'override runtime du mode côté 'tree' (contrairement à 'table',
          // setColumnReadOnly/setColumnReadWrite absents ici) — reflète directement l'attribut JSON.
          // `!== 'write'` (pas `=== 'read'`) : même comparaison que le rendu de cellule (ligne ~1088,
          // `col.mode === 'write'`) — une colonne sans `mode` explicite est donc en lecture par défaut.
          isReadOnly: () => col.mode !== 'write',
          // Remplace la liste d'items de l'éditeur (mode write) — même format que l'attribut JSON
          // `items` de la colonne. Passe par `colOverrides` (même mécanisme que TableField), donc
          // répercuté immédiatement sur le rendu (mergedCols → visibleCols/firstCol → colDefs).
          setComboEditorItems: items => {
            if (col.type !== 'Combo') throw new Error(`setComboEditorItems: la colonne "${col.code}" n'est pas de type Combo`)
            setColOverridesSynced(o => ({ ...o, [col.code]: { ...o[col.code], items } }))
          },
          // Items courants de l'éditeur (override s'il existe, sinon ceux du JSON d'origine) — lu
          // via colOverridesRef pour rester synchrone avec un setComboEditorItems() qui précède.
          getComboEditorItems: () => {
            if (col.type !== 'Combo') throw new Error(`getComboEditorItems: la colonne "${col.code}" n'est pas de type Combo`)
            return colOverridesRef.current[col.code]?.items ?? col.items ?? []
          },
        })),
      ],
      // Deux mécanismes indépendants déterminent la visibilité réellement rendue d'une colonne :
      // structurel (JSON `visible`, via colOverrides — une colonne structurellement masquée est
      // absente du modèle TanStack, cf. visibleCols) et bascule utilisateur (état TanStack
      // `columnVisibility`, panneau "Colonnes ▾" — une colonne JSON `visible:false` reste présente
      // dans le modèle, désactivée via cet état, réactivable depuis le panneau). Une colonne n'est
      // visible à l'écran que si les deux conditions passent. Même logique que TableField.jsx.
      isColumnVisible: code => {
        const col = leafColumnsRef.current.find(c => c.code === code)
        if (!col) throw new Error(`isColumnVisible: colonne "${code}" introuvable`)
        const structurallyVisible = { ...col, ...(colOverridesRef.current[code] ?? {}) }.visible !== false
        return structurallyVisible && columnVisibilityRef.current[code] !== false
      },
      // Même contrat que TableField.jsx — voir le commentaire détaillé là-bas. `_rowId` (entrée
      // synthétique de getColumnList(), absente de leafColumnsRef) lève la même exception
      // qu'isColumnVisible ci-dessus, pour la même raison : ce n'est pas une colonne réelle.
      scrollToColumn: code => {
        const col = leafColumnsRef.current.find(c => c.code === code)
        if (!col) throw new Error(`scrollToColumn: colonne "${code}" introuvable`)
        if (col.lock) return
        const container = scrollElRef.current
        const cellEl = container?.querySelector(`[data-tbs-col="${code}"]`)
        if (!container || !cellEl) return
        const containerRect = container.getBoundingClientRect()
        let lockedRight = containerRect.left
        // `th.style.left` (inline, posé par stickyTh pour lock/drag/rownum) — pas `position:
        // sticky` en getComputedStyle, posé par la classe CSS `.th` sur TOUS les en-têtes (fige
        // la ligne d'en-tête verticalement), ce qui rendait la méthode inopérante dans tous les
        // cas (bug constaté, cf. commentaire détaillé côté TableField.jsx).
        container.querySelectorAll('th').forEach(th => {
          if (th.style.left !== '') lockedRight = Math.max(lockedRight, th.getBoundingClientRect().right)
        })
        const cellRect = cellEl.getBoundingClientRect()
        if (cellRect.left < lockedRight) {
          container.scrollLeft -= lockedRight - cellRect.left
        } else if (cellRect.right > containerRect.right) {
          container.scrollLeft += cellRect.right - containerRect.right
        }
      },
      setColumnRenderer: (code, fn) => setColumnRenderers(o => ({ ...o, [code]: fn })),
      setColumnFilter:   (code, value) => setColumnFilters(prev => {
        const rest = prev.filter(f => f.id !== code)
        const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
        return empty ? rest : [...rest, { id: code, value }]
      }),
      clearColumnFilter: code => setColumnFilters(prev => prev.filter(f => f.id !== code)),
      clearAllColumnFilter: () => setColumnFilters([]),
      clearFilter:       () => setColumnFilters([]), // alias de clearAllColumnFilter()
      // Même geste que le panneau toolbar "Grouper par" : changer/annuler le groupement replie toutes
      // les branches ouvertes (setExpanded({})), même motif que l'onChange/le bouton ✕ — sauf si
      // `expanded` est truthy (setGrouping seulement, coercition large — `!!v`, même convention que
      // setVisible()/tbsGeneralApi.js — pas `=== true` strict, qui échouerait sur "true"/1 en
      // provenance de code métier faiblement typé), qui affiche au contraire tout développé
      // d'emblée (même valeur `true` que l'état initial par défaut, `collapseGrouping: false` —
      // cf. plus haut).
      // `codes` accepte un code unique (rétrocompatible) ou un tableau de codes (regroupement
      // sur plusieurs colonnes, même sélection multiple que le panneau toolbar "Grouper par" à
      // cases à cocher) — même contrat que TableField.jsx.
      setGrouping:   (codes, expanded) => { setGrouping(Array.isArray(codes) ? codes : (codes ? [codes] : [])); setExpanded(expanded ? true : {}) },
      clearGrouping: ()               => { setGrouping([]); setExpanded({}) },
      // Même geste qu'un clic sur l'en-tête de colonne (getToggleSortingHandler) : remplace tout
      // tri existant par ce seul critère. `dir` : "ASC"/"DESC" (insensible à la casse, défaut
      // "ASC" si absent) — même convention que l'attribut JSON `sortDir`.
      setSort:       (code, dir)      => setSorting([{ id: code, desc: (dir ?? 'ASC').toUpperCase() === 'DESC' }]),
      clearSort:     ()               => setSorting([]),
      enterCellEdition: (rowNum, colName) => {
        const flat = flattenNodes(rowsRef.current)
        if (rowNum < 0 || rowNum >= flat.length) throw new Error(`enterCellEdition: rowNum ${rowNum} hors bornes`)
        const container = document.querySelector(`[data-tbs-root="${id}"]`)
        if (!container) return
        const td = container.querySelector(`[data-tbs-row="${rowNum}"][data-tbs-col="${colName}"]`)
        if (!td) return
        td.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        const input = td.querySelector('input:not([type=hidden]), textarea, select')
        input?.focus()
      },

      reload: (extraParams) => {
        extraParamsRef.current = extraParams
        setLoadTick(t => t + 1)
      },
      // Force un nouveau rendu sans recharger les données (ex. après changement d'un état
      // externe lu par un renderer de colonne) — ne rappelle pas onTbsTreeLoader
      refresh: () => setRefreshTick(t => t + 1),

      validate: () => {
        const FORMAT_TYPES = new Set(['Date', 'DateTime', 'Heure', 'Montant', 'Numerique'])
        const writeCols = leafColumnsRef.current.filter(c => c.visible !== false && c.mode === 'write')
        let valid = true
        outer: for (const node of flattenNodes(rowsRef.current)) {
          for (const col of writeCols) {
            const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, node.id, col)
            if (READ_ONLY_EDITOR_TYPES.has(effectiveCol.type)) continue
            const v = node[col.code]
            if (v == null || v === '') continue
            const s = String(v)
            let ok = true
            if (effectiveCol.inputMask) {
              try { ok = new RegExp(`^(?:${effectiveCol.inputMask})$`).test(s) } catch { ok = true }
            }
            if (ok && FORMAT_TYPES.has(effectiveCol.type)) {
              if (effectiveCol.type === 'Date' || effectiveCol.type === 'DateTime')    ok = v instanceof Date && !isNaN(v.getTime())
              else if (effectiveCol.type === 'Heure')                        ok = typeof v === 'number' && !isNaN(v) && v >= 0 && v <= 24
              else if (effectiveCol.type === 'Montant' || effectiveCol.type === 'Numerique') ok = typeof v === 'number' && !isNaN(v)
            }
            if (!ok) { valid = false; break outer }
          }
        }
        setTreeError(valid ? null : 'Format de cellule invalide')
        return valid
      },

      // Alias backward-compat (méthodes renommées)
      getNbRows:           function() { return this.getNbNodes() },
      getSelectedRowIndex: function() { return this.getSelectedNodeIndex() },
      setSelectedRow:      function(n) { return this.setSelectedNode(n) },
      getSelectedRow:      function() { return this.getSelectedNode() },
      getRowAt:            function(n) { return this.getNodeAt(n) },
    }

    return () => { delete window.tbsComponents[id]; clearListeners(id) }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const leafCols = flattenLeafColumns(columns)
    setRowsSynced((fetchedData ?? []).map(n => parseTreeNode(n, leafCols)))
  }, [fetchedData]) // eslint-disable-line react-hooks/exhaustive-deps

  const dataLoadedRef = useRef(false)
  const wasLoadingRef = useRef(false)
  useEffect(() => {
    if (loading) { wasLoadingRef.current = true; return }
    if (!wasLoadingRef.current) return
    wasLoadingRef.current = false
    if (error) onEvent(id, 'onLoadTreeGridError')
    else onEvent(id, 'onLoadTreeGrid')
    if (!dataLoadedRef.current) { dataLoadedRef.current = true; onDataLoaded?.() }
  }, [loading, error, id, onEvent, onDataLoaded])

  const [sorting, setSorting]                   = useState(() =>
    sortColumn ? [{ id: sortColumn, desc: (sortDir ?? 'ASC').toUpperCase() === 'DESC' }] : []
  )
  const [columnFilters, setColumnFilters]       = useState([])
  // Colonnes masquées dans le JSON (`visible: false`) : initialement invisibles, mais présentes
  // dans le modèle TanStack (cf. flattenModelLeafColumns) — donc listées et réactivables depuis le
  // panneau "Colonnes ▾". Lecture unique au montage (comme `sorting`/`grouping` ci-dessous) : un
  // changement de `columns` après coup ne réinitialise pas cet état, piloté ensuite par l'utilisateur.
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const initial = {}
    for (const c of flattenModelLeafColumns(columns)) {
      if (c.visible === false) initial[c.code] = false
    }
    return initial
  })
  // Groupement initial — attribut `groupBy: true` porté directement par les colonnes (y compris
  // la colonne d'arborescence elle-même, 1re entrée de `columns`) : même règle que TableField.jsx.
  const [grouping, setGrouping]                 = useState(() =>
    flattenLeafColumns(columns).filter(c => c.groupBy).map(c => c.code)
  )
  const [expanded, setExpanded]                 = useState(() => collapseGrouping ? {} : true)
  const [showGroupPanel, setShowGroupPanel]     = useState(false)
  const groupPanelRef = useRef(null)
  useEffect(() => {
    if (!showGroupPanel) return
    const handler = e => { if (groupPanelRef.current && !groupPanelRef.current.contains(e.target)) setShowGroupPanel(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showGroupPanel])
  const [columnSizing, setColumnSizing]         = useState({})
  const [showColPanel, setShowColPanel]         = useState(false)
  const colPanelRef = useRef(null)
  useEffect(() => {
    if (!showColPanel) return
    const handler = e => { if (colPanelRef.current && !colPanelRef.current.contains(e.target)) setShowColPanel(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showColPanel])

  const containerRef = useRef(null)
  // Zone de défilement de la grille (`.tableScroll`, même élément que cible `fixedHeight`) — distincte
  // du div racine [data-tbs-root] (titre + toolbar + arbre + pagination). setHeight()/getHeight() sont
  // surchargées ci-dessus (registration `window.tbsComponents[id]`) pour cibler cette ref plutôt que
  // la racine — même raison que TableField.jsx : `.tableCard` (`overflow:hidden`) n'est pas étiré en
  // hauteur par le `flex-col` racine, donc redimensionner la racine seule tronque les lignes en trop
  // sans ascenseur (`.tableScroll` gardant sa hauteur naturelle) au lieu de contrôler la zone de
  // défilement comme le fait `fixedHeight` (attribut JSON).
  const scrollElRef = useRef(null)
  // `resizable` (attribut JSON) : poignée de redimensionnement en bas de l'arbre, cible `.tableScroll`
  // (même raison/même élément que `fixedHeight`/`setHeight()` ci-dessus) — hauteur uniquement, la
  // largeur reste pilotée par les colonnes/le conteneur parent. `resizedHeight` (px, mesuré au
  // premier drag) prime alors sur `fixedHeight` une fois posé par l'utilisateur ; `fixedHeight` reste
  // la valeur JSON d'origine, jamais réécrite (redimensionnement manuel seulement, pas persisté
  // au-delà de la session). Même schéma mousedown/mousemove/mouseup + nettoyage au démontage que le
  // splitter de CanvasField.jsx (SplitterPanes) et TableField.jsx — pas de constante partagée.
  const [resizedHeight, setResizedHeight] = useState(null)
  const activeResizeHandlersRef = useRef(null)
  const handleResizeMouseDown = useCallback(e => {
    e.preventDefault()
    const el = scrollElRef.current
    if (!el) return
    const startY = e.clientY
    const startHeight = el.getBoundingClientRect().height
    const move = ev => setResizedHeight(Math.max(RESIZE_MIN_HEIGHT, startHeight + (ev.clientY - startY)))
    const up = () => {
      activeResizeHandlersRef.current = null
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
    }
    activeResizeHandlersRef.current = { move, up }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }, [])
  useEffect(() => () => {
    const active = activeResizeHandlersRef.current
    if (!active) return
    document.removeEventListener('mousemove', active.move)
    document.removeEventListener('mouseup', active.up)
  }, [])
  const [containerWidth, setContainerWidth] = useState(0)
  useLayoutEffect(() => {
    if (collapsed) return
    const el = containerRef.current
    if (el) setContainerWidth(el.clientWidth)
  }, [collapsed])

  useEffect(() => {
    if (!fitColumns || collapsed) return
    const handleResize = () => {
      const el = containerRef.current
      if (el) setContainerWidth(el.clientWidth)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [fitColumns, collapsed])

  // modelCols : toutes les feuilles participant au modèle TanStack (cf. flattenModelLeafColumns —
  // exclut seulement les feuilles sous un groupe masqué). mergedCols applique les overrides runtime
  // par colonne (colOverrides, ex. setComboEditorItems() — même mécanisme que TableField). visibleCols
  // (JSON + overrides + `visible` structurel, sans lien avec `columnVisibility`) pilote la largeur/le
  // pied de tableau/les colonnes figées, comme avant. `firstCol` (colonne d'arborescence) est dérivée
  // de `mergedCols` (jamais exclue, même avec un `visible: false` malencontreux) et reste garantie
  // être une feuille (restriction TreeField) en position 0.
  const modelCols   = useMemo(() => flattenModelLeafColumns(columns), [columns])
  const mergedCols  = useMemo(() => modelCols.map(c => ({ ...c, ...(colOverrides[c.code] ?? {}) })), [modelCols, colOverrides])
  const visibleCols = useMemo(() => mergedCols.filter(c => c.visible !== false), [mergedCols])
  const firstCol    = mergedCols[0]

  // Index depth-first pour chaque nœud — pour les attributs data-tbs-row
  const flatIdxMap = useMemo(() => {
    const map = new Map()
    flattenNodes(rows).forEach((n, i) => map.set(n.id, i))
    return map
  }, [rows])

  const colDefs = useMemo(() => {
    const GROUPABLE_TYPES = new Set(['Code', 'Combo', 'Date', 'DateTime', 'Heure', 'Montant', 'Numerique', 'Texte'])
    const someHaveWeight = visibleCols.some(c => c.columnWeight != null)
    const prefixPx = (dragDropId ? DRAG_WIDTH : 0) + (rowNumber ? ROWNUM_WIDTH : 0)

    let colSize
    if (fitColumns && !someHaveWeight) {
      const containerForData = Math.max(100, containerWidth - prefixPx)
      const n = visibleCols.length
      const eq = n > 0 ? Math.max(50, Math.floor(containerForData / n)) : 150
      colSize = () => eq
    } else {
      const hasWeights = someHaveWeight
      const totalWeight = hasWeights ? visibleCols.reduce((s, c) => s + (c.columnWeight ?? 0), 0) : 0
      const fixedWidth = hasWeights
        ? visibleCols.filter(c => c.columnWeight == null)
            .reduce((s, c) => s + (c.width ?? (c.code === firstCol?.code ? 220 : 120)), 0)
        : 0
      const availableWidth = Math.max(0, containerWidth - prefixPx - fixedWidth)
      colSize = (col, defaultPx) =>
        hasWeights && col.columnWeight != null && availableWidth > 0
          ? Math.max(50, Math.floor((col.columnWeight / totalWeight) * availableWidth))
          : col.width ?? defaultPx
    }

    const treeCol = firstCol ? [{
      id:          firstCol.code,
      accessorKey: firstCol.code,
      header:      () => (
        // '&nbsp;' en secours : un libellé vide laisserait .thLabel sans aucun contenu texte, qui
        // s'effondre à 0px de hauteur en layout flex — désalignant le filtre sous l'en-tête par
        // rapport aux colonnes à libellé non vide (même ligne d'en-tête).
        <span className="flex items-center gap-1.5" style={{ minWidth: 0 }} title={firstCol.tooltipText || stripHtml(firstCol.label)}>
          {firstCol.icon && <i className={firstCol.icon} aria-hidden="true" style={{ flexShrink: 0 }} />}
          <span className={styles.thLabel} dangerouslySetInnerHTML={{ __html: firstCol.label || '&nbsp;' }} />
        </span>
      ),
      size:        fitColumns && !someHaveWeight ? colSize() : colSize(firstCol, 220),
      minSize:     80,
      enableSorting:  firstCol.sort ?? true,
      enableResizing: true,
      // Groupable pour tout type de colonne compatible (GROUPABLE_TYPES) — même règle que
      // TableField.jsx.
      enableGrouping: GROUPABLE_TYPES.has(firstCol.type),
      // `getGroupingValue` reçoit le nœud de donnée brut (TData), pas un Row TanStack — accès
      // direct à la propriété, jamais `row.getValue()` (absent, ferait planter le calcul du
      // modèle groupé). Groupement multi-colonnes (panneau "Grouper par", plusieurs cases
      // cochées) : seule la 1re colonne cochée (`grouping[0]`) est effectivement transmise à
      // TanStack comme colonne de groupement (`effectiveGrouping` plus bas) — même règle que
      // TableField.jsx. Quand cette colonne est l'ancre et que plusieurs colonnes sont cochées,
      // sa valeur de groupement devient la clé composite (jointure des valeurs formatées de
      // chaque colonne cochée, dans l'ordre de coche).
      getGroupingValue: row => {
        if (grouping.length > 1 && grouping[0] === firstCol.code) {
          return grouping.map(code => {
            const gcol = mergedCols.find(c => c.code === code)
            return gcol ? fmtSummaryValue(gcol.type, row[code]) : String(row[code] ?? '')
          }).join('\u0000')
        }
        return firstCol.type === 'DateTime' ? (formatFrDateTime(row[firstCol.code]) ?? '')
          : firstCol.type === 'Date' ? (formatFrDate(row[firstCol.code]) ?? '')
          : row[firstCol.code]
      },
      meta: { col: firstCol },
      footer: firstCol.summaryType
        ? ({ table }) => computeSummary(firstCol.summaryType, table.getFilteredRowModel().flatRows.filter(r => !r.getIsGrouped()), firstCol.code, firstCol.type)
        : undefined,
      cell: ({ row, getValue }) => {
        const orig = row.original
        const isLeaf = orig._isLeaf || !row.getCanExpand()
        const nodeIcon = orig._icon ?? (isLeaf ? 'fa-regular fa-file' : (row.getIsExpanded() ? 'fa-solid fa-folder-open' : 'fa-solid fa-folder'))
        return (
          <div style={{ paddingLeft: row.depth * 20, display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              type="button"
              className={styles.treeExpandBtn}
              style={isLeaf ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => { e.stopPropagation(); row.getToggleExpandedHandler()() }}
            >
              {row.getIsExpanded() ? '▼' : '▶'}
            </button>
            <i className={nodeIcon} aria-hidden="true" style={{ flexShrink: 0, fontSize: '0.85em' }} />
            {fmtTreeCellValue(firstCol.type, getValue())}
          </div>
        )
      },
    }] : []

    // Colonne feuille — reconstruit à l'identique pour chaque feuille de l'arbre `columns` (hors
    // colonne d'arborescence, gérée par `treeCol` ci-dessus).
    const buildLeafColumnDef = col => {
      // Date/DateTime sont stockées en objet Date (référence) : le groupement (Map par valeur de
      // clé) et le filtre texte doivent passer par la représentation "JJ/MM/AAAA" formatée plutôt
      // que par l'objet Date brut, sans quoi deux dates identiques ne seraient jamais regroupées
      // (Date !== Date par référence) et le filtre chercherait dans un Date.toString() illisible.
      const isDateLike = col.type === 'Date' || col.type === 'DateTime'
      const fmtDate = col.type === 'DateTime' ? formatFrDateTime : formatFrDate
      return {
        id:          col.code,
        accessorKey: col.code,
        header:      () => (
          // '&nbsp;' en secours : voir note sur treeCol ci-dessus.
          <span className="flex items-center gap-1.5" style={{ minWidth: 0 }} title={col.tooltipText || stripHtml(col.label)}>
            {col.icon && <i className={col.icon} aria-hidden="true" style={{ flexShrink: 0 }} />}
            <span className={styles.thLabel} dangerouslySetInnerHTML={{ __html: col.label || '&nbsp;' }} />
          </span>
        ),
        size:        fitColumns && !someHaveWeight ? colSize() : colSize(col, 120),
        minSize:     50,
        enableSorting:      col.sort ?? true,
        enableResizing:     true,
        // Groupable pour tout type de colonne compatible (GROUPABLE_TYPES) — même règle que
        // TableField.jsx.
        enableGrouping:     GROUPABLE_TYPES.has(col.type),
        enableColumnFilter: ['Texte', 'Combo', 'Date', 'Montant', 'Numerique', 'Code'].includes(col.type),
        ...(['Numerique', 'Montant'].includes(col.type) && { sortingFn: numericSortingFn }),
        // Groupement multi-colonnes : voir le commentaire équivalent sur `treeCol`/`firstCol`
        // ci-dessus (même règle que TableField.jsx).
        getGroupingValue: row => {
          if (grouping.length > 1 && grouping[0] === col.code) {
            return grouping.map(code => {
              const gcol = mergedCols.find(c => c.code === code)
              return gcol ? fmtSummaryValue(gcol.type, row[code]) : String(row[code] ?? '')
            }).join('\u0000')
          }
          return isDateLike ? (fmtDate(row[col.code]) ?? '') : row[col.code]
        },
        ...(isDateLike && {
          filterFn: (row, colId, filterValue) => {
            if (!filterValue) return true
            const cell = (fmtDate(row.getValue(colId)) ?? '').toLowerCase()
            return cell.includes(String(filterValue).toLowerCase())
          },
        }),
        meta: { col },
        footer: col.summaryType
          ? ({ table }) => computeSummary(col.summaryType, table.getFilteredRowModel().flatRows.filter(r => !r.getIsGrouped()), col.code, col.type)
          : undefined,
        cell: info => {
          const v = info.getValue()
          const nodeId = info.row.original.id
          const effectiveCol = resolveEffectiveCol(cellEditorOverridesRef.current, nodeId, col)
          const renderer = columnRenderers[effectiveCol.code]
          const isEditableCol = col.mode === 'write' && !READ_ONLY_EDITOR_TYPES.has(effectiveCol.type)
          // Un renderer personnalisé (setColumnRenderer) s'applique aussi aux colonnes en écriture
          // — pas seulement en lecture seule — mais le nœud doit rester éditable : voir
          // EditableRenderedTreeCell (bascule affichage rendu / éditeur réel au clic-perte de focus).
          if (isEditableCol && typeof renderer === 'function') {
            return <EditableRenderedTreeCell col={effectiveCol} value={v} rowIndex={info.row.index} nodeId={nodeId} onEvent={onEvent} treeId={id} onCellChange={onTreeCellChangeRef} renderer={renderer} />
          }
          if (isEditableCol) {
            return <TreeCellEditor col={effectiveCol} value={v} nodeId={nodeId} rowIndex={info.row.index} onEvent={onEvent} treeId={id} onCellChange={onTreeCellChangeRef} />
          }
          return <TreeCellDisplay col={effectiveCol} value={v} rowIndex={info.row.index} renderer={renderer} />
        },
      }
    }

    // Colonne de groupement (attribut JSON `children`) — en-tête chapeautant récursivement ses
    // enfants ; jamais triable/filtrable/redimensionnable elle-même (ces attributs, ainsi que
    // `lock`, ne concernent que les feuilles — cf. modelByCode). Un groupe masqué (`visible:
    // false`) est omis entièrement (lui et son sous-arbre) ; une feuille masquée, elle, obtient
    // quand même un colDef (initialement invisible via `columnVisibility`) — cf.
    // flattenModelLeafColumns. Ne s'applique qu'aux colonnes de données (jamais à la colonne
    // d'arborescence, gérée séparément par `treeCol`).
    const modelByCode = Object.fromEntries(mergedCols.filter(c => c.code !== firstCol?.code).map(c => [c.code, c]))
    const buildColumnDefTree = entries => {
      const result = []
      for (const entry of entries ?? []) {
        if (entry.children) {
          if (entry.visible === false) continue
          const childDefs = buildColumnDefTree(entry.children)
          if (childDefs.length === 0) continue
          result.push({
            id: entry.code,
            header: () => (
              <span className="flex items-center justify-center gap-1.5" style={{ minWidth: 0 }} title={entry.tooltipText || stripHtml(entry.label)}>
                {entry.icon && <i className={entry.icon} aria-hidden="true" style={{ flexShrink: 0 }} />}
                <span className={styles.thLabel} dangerouslySetInnerHTML={{ __html: entry.label ?? '' }} />
              </span>
            ),
            columns: childDefs,
          })
        } else {
          const col = modelByCode[entry.code]
          if (col) result.push(buildLeafColumnDef(col))
        }
      }
      return result
    }
    const dataCols = buildColumnDefTree(columns.slice(1))

    const prefixCols = []
    if (dragDropId) {
      prefixCols.push({
        id: '__drag__', accessorKey: '__drag__',
        header: () => null, cell: () => null,
        size: DRAG_WIDTH, minSize: DRAG_WIDTH,
        enableSorting: false, enableResizing: false, enableGrouping: false, enableColumnFilter: false,
        meta: { drag: true },
      })
    }
    if (rowNumber) {
      prefixCols.push({
        id: '__rownum__', accessorKey: '__rownum__',
        header: () => '#', cell: () => null,
        size: ROWNUM_WIDTH, minSize: ROWNUM_WIDTH,
        enableSorting: false, enableResizing: false, enableGrouping: false, enableColumnFilter: false,
        meta: { rownum: true },
      })
    }

    const spacer = { id: '__spacer__', accessorKey: '__spacer__', header: () => null, cell: () => null, size: 0, minSize: 0, enableSorting: false, enableResizing: false, enableGrouping: false, enableColumnFilter: false, meta: { spacer: true } }

    return [...prefixCols, ...treeCol, ...dataCols, spacer]
  }, [columns, visibleCols, containerWidth, fitColumns, dragDropId, rowNumber, columnRenderers, grouping]) // eslint-disable-line react-hooks/exhaustive-deps

  // Un seul niveau de groupement réellement transmis à TanStack (1re colonne cochée) — même
  // raisonnement que TableField.jsx (`getGroupingValue` ci-dessus gère la clé composite).
  const effectiveGrouping = useMemo(() => (grouping.length > 0 ? [grouping[0]] : []), [grouping])

  // Sticky (lock) — cumulative left offset per locked column
  const lockedOffsets = useMemo(() => {
    const offsets = {}
    let left = 0
    if (dragDropId) { offsets['__drag__'] = left; left += DRAG_WIDTH }
    if (rowNumber)  { offsets['__rownum__'] = left; left += ROWNUM_WIDTH }
    for (const col of visibleCols) {
      if (col.lock) {
        offsets[col.code] = left
        left += columnSizing[col.code] ?? col.width ?? (col === firstCol ? 220 : 120)
      }
    }
    return offsets
  }, [visibleCols, columnSizing, firstCol, dragDropId, rowNumber])

  const stickyTh = useCallback((colId) => {
    if (!(colId in lockedOffsets)) return {}
    return { position: 'sticky', left: lockedOffsets[colId], zIndex: 3, background: '#f0f4f9' }
  }, [lockedOffsets])

  const stickyTd = useCallback((colId, isSelected, isHovered) => {
    if (!(colId in lockedOffsets)) return {}
    return { position: 'sticky', left: lockedOffsets[colId], zIndex: 1, background: (isSelected || isHovered) ? '#e0eaf7' : '#fff' }
  }, [lockedOffsets])

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data: rows,
    columns: colDefs,
    state: { sorting, columnFilters, columnVisibility, grouping: effectiveGrouping, expanded, columnSizing },
    onSortingChange:          setSorting,
    onColumnFiltersChange:    setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onExpandedChange:         setExpanded,
    onColumnSizingChange:     setColumnSizing,
    getCoreRowModel:          getCoreRowModel(),
    getSortedRowModel:        getSortedRowModel(),
    getFilteredRowModel:      getFilteredRowModel(),
    getGroupedRowModel:       getGroupedRowModel(),
    getExpandedRowModel:      getExpandedRowModel(),
    getSubRows:               row => row.children,
    getRowId:                 row => row.id,
    columnResizeMode:         'onChange',
    // Même raisonnement que TableField.jsx : sans cela, TanStack déplacerait par défaut la colonne
    // ancre de groupement en tête de la liste complète des colonnes feuilles (devant `__drag__`/
    // `__rownum__` inclus) dès qu'un groupement est actif, désynchronisant l'ordre des colonnes de
    // données vis-à-vis de l'en-tête.
    groupedColumnMode:        false,
    autoResetExpanded:        false,
  })

  const hasFooter     = visibleCols.some(c => c.summaryType)
  // table.getAllLeafColumns() (et non colDefs, qui peut désormais contenir des colonnes de
  // groupement sans enableGrouping propre) — aplati nativement par TanStack.
  const groupableCols = table.getAllLeafColumns().filter(c => c.columnDef.enableGrouping)

  const handleRowClick = useCallback(row => {
    const orig = row.original
    // Nœud déjà sélectionné (re-clic) : ni changement d'état ni event, évite un onTreeGridSelRow
    // redondant sans changement de sélection.
    if (selectedIdRef.current === orig.id) return
    selectNode(orig.id)
    onEvent(id, 'onTreeGridSelRow', row.index, null)
  }, [id, onEvent, selectNode])

  const handleDragStart = useCallback((e, rowId) => {
    window.__tbsDragContext = { tableId: id, rowId, dragDropId }
    e.dataTransfer.effectAllowed = 'move'
  }, [id, dragDropId])

  const handleDragEnd = useCallback(() => {
    window.__tbsDragContext = null
    setDragOverRowId(null)
    setDragOverPos(null)
  }, [])

  const handleDragOverRow = useCallback((e, rowId, isLeaf) => {
    const ctx = window.__tbsDragContext
    if (!ctx || ctx.dragDropId !== dragDropId) return
    e.preventDefault()
    setDragOverRowId(rowId)
    setDragOverPos(computeDropPosition(e, isLeaf))
  }, [dragDropId])

  const handleDropOnRow = useCallback((e, destRowId, isLeaf) => {
    e.preventDefault()
    const ctx = window.__tbsDragContext
    if (!ctx || ctx.dragDropId !== dragDropId) return
    const position = destRowId === null ? 'after' : computeDropPosition(e, isLeaf)
    safeInvoke(treeDragDropCallback || window.onTbsTreeDragDropCallback, ctx.tableId, id, ctx.rowId, destRowId, position, e.shiftKey)
    setDragOverRowId(null)
    setDragOverPos(null)
    window.__tbsDragContext = null
  }, [id, dragDropId, treeDragDropCallback])

  // Row numbering — all visible nodes numbered sequentially
  let rowNums = null
  if (rowNumber) {
    rowNums = new Map()
    let idx = 0
    for (const row of table.getRowModel().rows) {
      rowNums.set(row.id, ++idx)
    }
  }

  const treeErrorOutline = treeError ? { outline: '2px solid hsl(var(--destructive))' } : undefined

  // Collapsed state — render only the title bar
  if (collapsed && collapsible) {
    return (
      <div id={id} data-tbs-root={id} className={cn(styles.field, cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
        <div className={styles.tableCard} onClick={() => setCollapsed(false)} style={{ cursor: 'pointer', ...treeErrorOutline }}>
          <div className={styles.collapseBar}>
            <i className="fa-solid fa-chevron-right fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
            {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
            <span className={styles.collapseBarLabel}>{title}</span>
          </div>
        </div>
        {treeError && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginTop: 4 }}>{treeError}</p>}
      </div>
    )
  }

  // Extraits pour être réutilisés à la fois dans la barre de titre repliable (collapsible : fusionnés
  // sur une seule ligne avec le titre) et dans la toolbar classique (non collapsible : ligne séparée)
  // — évite la duplication entre les deux mises en page ci-dessous.
  const toolbarButtonsContent = (
    <>
      <Button size="sm" variant="ghost" onClick={() => setExpanded(true)}>Tout déplier</Button>
      <Button size="sm" variant="ghost" onClick={() => setExpanded({})}>Tout replier</Button>
      {toolbarState.length > 0 && <span className={styles.toolbarSep} />}
      {toolbarState.map(btn => (
        <Button
          key={btn.id}
          size="sm"
          variant="ghost"
          title={btn.tooltip}
          disabled={btn.active === false}
          className="tbs-toolbar-btn-size"
          style={{ visibility: btn.visible === false ? 'hidden' : 'visible' }}
          onClick={btn.active !== false ? () => onEvent(id, 'onTblToolbarClick', 0, btn.id) : undefined}
        >
          {btn.icon && <i className={btn.icon} aria-hidden="true" />}
        </Button>
      ))}
    </>
  )
  // Coche/décoche une colonne "Grouper par" — l'ordre de coche détermine l'ordre de la clé
  // composite (1er coché = 1re colonne ancre pour TanStack, cf. `effectiveGrouping`/
  // `getGroupingValue`), conforme à `GROUP BY c1, c2` SQL.
  const toggleGroupingCol = code => {
    setGrouping(g => g.includes(code) ? g.filter(c => c !== code) : [...g, code])
    setExpanded({})
  }
  const groupingColumnsContent = (
    <>
      {/* Toujours rendu (contrairement à "Grouper par" ci-dessous) : rappelle onTbsTreeLoader —
          onTbsTreeLoader n'a pas de page/tri côté serveur (contrairement à onTbsGridLoader), donc
          pas de conditions courantes à préserver ici, juste un rechargement forcé des données.
          Même mécanisme que TableField.jsx (bump de loadTick, déjà dans les deps de l'effet de
          chargement). */}
      <Button size="sm" variant="ghost" title={t('reloadTable')} onClick={() => setLoadTick(v => v + 1)}>
        <RefreshCw size={14} />
      </Button>
      {groupableCols.length > 0 && (
        <div style={{ position: 'relative' }} ref={groupPanelRef}>
          <Button size="sm" variant="outline" onClick={() => setShowGroupPanel(v => !v)}>
            <span>{t('groupBy')}{grouping.length > 0 ? ` (${grouping.length})` : ''}</span>
            {grouping.length > 0 && (
              <span
                className="ml-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                onClick={e => { e.stopPropagation(); setGrouping([]); setExpanded({}) }}
              >
                <X size={12} />
              </span>
            )}
          </Button>
          {showGroupPanel && (
            <div className={styles.colPanel}>
              {grouping.length > 0 && (
                <button type="button" className={styles.colPanelReset} onClick={() => { setGrouping([]); setExpanded({}) }}>
                  {t('resetGrouping')}
                </button>
              )}
              {groupableCols.map(c => (
                <label key={c.id} className={styles.colPanelItem}>
                  <input type="checkbox" checked={grouping.includes(c.id)} onChange={() => toggleGroupingCol(c.id)} />
                  {stripHtml(c.columnDef.meta.col.label)}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      <div style={{ position: 'relative' }} ref={colPanelRef}>
        <Button size="sm" variant="outline" onClick={() => setShowColPanel(v => !v)}>{t('columns')}</Button>
        {showColPanel && (
          <div className={styles.colPanel}>
            {table.getAllLeafColumns()
              .filter(col => !col.columnDef.meta?.spacer && !col.columnDef.meta?.drag && !col.columnDef.meta?.rownum)
              .map(col => (
                <label key={col.id} className={styles.colPanelItem}>
                  <input type="checkbox" checked={col.getIsVisible()} onChange={col.getToggleVisibilityHandler()} />
                  {stripHtml(col.columnDef.meta?.col?.label) || col.id}
                </label>
              ))
            }
          </div>
        )}
      </div>
    </>
  )

  return (
    <div id={id} data-tbs-root={id} className={cn(styles.field, cssClass)} style={{ ...cssStyle, ...visibilityStyle }}>
      {(title || icon) && !collapsible && (
        <label className={`${styles.label} flex items-center gap-1.5`}>
          {icon && <i className={`${icon} text-[hsl(var(--muted-foreground))]`} aria-hidden="true" />}
          {title}
        </label>
      )}
      <div className={styles.tableCard} ref={containerRef} style={treeErrorOutline}>
        {/* Collapse bar — titre + trigger de repli, toolbar et groupement/colonnes fusionnés sur
            cette même ligne (pas de ligne "toolbar" séparée en dessous, contrairement au cas non
            collapsible) — `stopPropagation` sur ces deux blocs : sans lui, cliquer un bouton/select
            déclencherait aussi le repli de la carte (onClick posé sur toute la ligne). */}
        {collapsible && (
          <div className={`${styles.collapseBar} ${styles.collapseBarExpanded}`} onClick={() => setCollapsed(true)}>
            <i className="fa-solid fa-chevron-down fa-xs" style={{ color: '#7a8499', flexShrink: 0 }} aria-hidden="true" />
            {icon && <i className={icon} style={{ color: '#7a8499', flexShrink: 0, marginLeft: 2 }} aria-hidden="true" />}
            <span className={styles.collapseBarLabel}>{title}</span>
            {toolbarVisible && (
              <div className={styles.toolbarLeft} onClick={e => e.stopPropagation()}>{toolbarButtonsContent}</div>
            )}
            <div className={styles.toolbarRight} onClick={e => e.stopPropagation()}>{groupingColumnsContent}</div>
          </div>
        )}

        {/* ── Toolbar (non collapsible uniquement — sinon fusionnée dans le collapse bar ci-dessus) ──
            `toolbarVisible` ne cache que le cadre des boutons `toolbarBtns` (JSON), pas les contrôles
            intégrés (reload/grouper par/colonnes) à droite. */}
        {!collapsible && <div className={styles.toolbar}>
          {toolbarVisible && <div className={styles.toolbarLeft}>{toolbarButtonsContent}</div>}
          <div className={styles.toolbarRight}>{groupingColumnsContent}</div>
        </div>}

        {/* ── Table ───────────────────────────────────────────── */}
        <div ref={scrollElRef} className={styles.tableScroll} style={(resizedHeight != null || fixedHeight)
          ? { height: resizedHeight != null ? `${resizedHeight}px` : toCssLength(fixedHeight), overflowY: 'auto' }
          : undefined}>
          <table className={styles.tbl} style={{ width: '100%', ...(fitColumns ? {} : { minWidth: table.getTotalSize() }) }}>
            {/* table-layout:fixed n'honore que la 1re <tr> en l'absence de <colgroup> ; avec
                un en-tête multi-lignes (groupement), la 1re ligne réelle n'est plus celle des
                feuilles — sans colgroup le navigateur ignorerait les largeurs (redimensionnement
                muet, colonnes réparties à l'identique). */}
            <colgroup>
              {table.getVisibleLeafColumns().map(col => (
                <col key={col.id} style={col.columnDef.meta?.spacer ? undefined : { width: col.getSize() }} />
              ))}
            </colgroup>
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id}>
                  {hg.headers.map(header => {
                    // Colonne feuille non groupée à côté d'un groupe voisin : TanStack lui donne
                    // un en-tête "réel" uniquement à la ligne la plus profonde (celle des feuilles)
                    // ; aux lignes plus hautes, elle n'a qu'une cellule de remplissage vide
                    // (isPlaceholder) pour préserver l'alignement des colonnes — aucun rowSpan ne
                    // relie les deux, ce sont deux `<th>` distincts empilés.
                    if (header.isPlaceholder) {
                      return <th key={header.id} colSpan={header.colSpan} className={styles.thGroup} />
                    }
                    const isSpacer = header.column.columnDef.meta?.spacer
                    const isDrag   = header.column.columnDef.meta?.drag
                    const isRownum = header.column.columnDef.meta?.rownum
                    if (isSpacer) return <th key={header.id} className={styles.thSpacer} />
                    if (isDrag)   return <th key={header.id} className={styles.dragHandleTh} style={{ width: DRAG_WIDTH, ...stickyTh('__drag__') }} />
                    if (isRownum) return <th key={header.id} className={styles.rowNumTh} style={{ width: ROWNUM_WIDTH, ...stickyTh('__rownum__') }}>#</th>
                    // Colonne de groupement (attribut JSON `children`) — en-tête chapeautant,
                    // jamais triable/filtrable/redimensionnable (voir buildColumnDefTree).
                    if (header.subHeaders.length > 0) {
                      const rightmostLeaf = getRightmostLeafHeader(header)
                      return (
                        <th key={header.id} colSpan={header.colSpan} className={styles.thGroup}>
                          <span className="flex items-center justify-center gap-1.5" style={{ whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          <div
                            className={`${styles.resizer} ${rightmostLeaf.column.getIsResizing() ? styles.resizerActive : ''}`}
                            onMouseDown={rightmostLeaf.getResizeHandler()}
                            onTouchStart={rightmostLeaf.getResizeHandler()}
                          />
                        </th>
                      )
                    }
                    return (
                      <th key={header.id} data-tbs-col={header.column.id} colSpan={header.colSpan} className={`${styles.th} ${header.column.id in lockedOffsets ? styles.thLocked : ''}`} style={{ width: header.getSize(), ...stickyTh(header.column.id) }}>
                        <>
                          <div className={styles.thInner}>
                            <span
                              className={header.column.getCanSort() ? styles.sortable : ''}
                              style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', minWidth: 0, overflow: 'hidden' }}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <span style={{ fontSize: '1.1em', marginRight: '0.25em', flexShrink: 0 }}>{{ asc: '↑', desc: '↓' }[header.column.getIsSorted()] ?? ''}</span>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                          </div>
                          {header.column.getCanFilter() && (
                            <input
                              className={styles.thFilter}
                              value={header.column.getFilterValue() ?? ''}
                              onChange={e => header.column.setFilterValue(e.target.value)}
                              placeholder={t('filter')}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                          <div
                            className={`${styles.resizer} ${header.column.getIsResizing() ? styles.resizerActive : ''}`}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                          />
                        </>
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={table.getVisibleLeafColumns().length} className={styles.tdMsg}>Chargement…</td></tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr><td colSpan={table.getVisibleLeafColumns().length} className={styles.tdMsg}>{t('noData')}</td></tr>
              ) : table.getRowModel().rows.map(row => {
                const isSelected = row.original.id === selectedId
                const isHovered  = row.original.id === hoveredId
                const flatIdx = flatIdxMap.get(row.original.id)
                return (
                  <tr
                    key={row.id}
                    // Identifiant stable de nœud, indépendant du tri/filtre affiché — permet à
                    // addNode() de faire défiler jusqu'au nœud ajouté, voir TableField.jsx pour le
                    // même raisonnement (data-tbs-row-id/scrollRowIntoView).
                    data-tbs-row-id={row.original.id}
                    className={[
                      styles.tr,
                      row.getCanExpand() ? styles.trParent : styles.trLeaf,
                      isSelected ? styles.trSelected : '',
                      dragDropId && dragOverRowId === row.original.id && dragOverPos === 'before' ? styles.trDragOverBefore : '',
                      dragDropId && dragOverRowId === row.original.id && dragOverPos === 'after'  ? styles.trDragOverAfter  : '',
                      dragDropId && dragOverRowId === row.original.id && dragOverPos === 'append'  ? styles.trDragOver       : '',
                      ...(rowClassOverrides[row.original.id] ?? []),
                    ].join(' ')}
                    draggable={!!dragDropId}
                    onDragStart={dragDropId ? e => handleDragStart(e, row.original.id) : undefined}
                    onDragEnd={dragDropId ? handleDragEnd : undefined}
                    onDragOver={dragDropId ? e => handleDragOverRow(e, row.original.id, row.original._isLeaf) : undefined}
                    onDragLeave={dragDropId ? e => { if (!e.currentTarget.contains(e.relatedTarget)) { setDragOverRowId(null); setDragOverPos(null) } } : undefined}
                    onDrop={dragDropId ? e => handleDropOnRow(e, row.original.id, row.original._isLeaf) : undefined}
                    onMouseDown={() => handleRowClick(row)}
                    onDoubleClick={() => onEvent(id, 'onRowTreeGridClicked', row.index, 0)}
                    onMouseEnter={() => setHoveredId(row.original.id)}
                    onMouseLeave={() => setHoveredId(hid => (hid === row.original.id ? null : hid))}
                  >
                    {row.getVisibleCells().map(cell => {
                      const isSpacer = cell.column.columnDef.meta?.spacer
                      const isDrag   = cell.column.columnDef.meta?.drag
                      const isRownum = cell.column.columnDef.meta?.rownum
                      if (isSpacer) return <td key={cell.id} className={styles.tdSpacer} />
                      if (isDrag) return (
                        <td key={cell.id} className={styles.dragHandleTd} style={{ width: DRAG_WIDTH, ...stickyTd('__drag__', isSelected, isHovered) }}>
                          <span className={styles.dragHandle}>⠿</span>
                        </td>
                      )
                      if (isRownum) return (
                        <td key={cell.id} className={`${styles.td} ${styles.rowNumTd}`} style={{ width: ROWNUM_WIDTH, ...stickyTd('__rownum__', isSelected, isHovered) }}>
                          {rowNums?.get(row.id) ?? ''}
                        </td>
                      )
                      return (
                        <td key={cell.id}
                          data-tbs-row={flatIdx}
                          data-tbs-col={cell.column.id}
                          className={`${styles.td} ${cell.column.id in lockedOffsets ? styles.tdLocked : ''}`}
                          style={stickyTd(cell.column.id, isSelected, isHovered)}
                          onClick={() => {
                            // Index parmi les colonnes réellement rendues (table.getVisibleLeafColumns,
                            // hors utilitaires) — et non `visibleCols` (JSON, structurel) : une colonne
                            // masquée dans le JSON puis réactivée via le panneau "Colonnes ▾" doit obtenir
                            // un index correct, pas -1 (absente de `visibleCols`).
                            const colIdx = table.getVisibleLeafColumns()
                              .filter(c => !c.columnDef.meta?.spacer && !c.columnDef.meta?.drag && !c.columnDef.meta?.rownum)
                              .findIndex(c => c.id === cell.column.id)
                            onEvent(id, 'onTreeCellClicked', row.index, colIdx)
                          }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
              {/* Drop zone — always rendered when dragDropId is set */}
              {dragDropId && (
                <tr
                  className={dragOverRowId === '__end__' ? styles.trDragOverAfter : ''}
                  onDragOver={e => {
                    const ctx = window.__tbsDragContext
                    if (!ctx || ctx.dragDropId !== dragDropId) return
                    e.preventDefault()
                    setDragOverRowId('__end__')
                    setDragOverPos('after')
                  }}
                  onDrop={e => handleDropOnRow(e, null, true)}
                  onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) { setDragOverRowId(v => v === '__end__' ? null : v); setDragOverPos(null) } }}
                >
                  <td colSpan={table.getVisibleLeafColumns().length} className={styles.dropZoneTd} />
                </tr>
              )}
            </tbody>
            {hasFooter && (
              <tfoot>
                {/* Une seule ligne, une cellule par colonne feuille (dans l'ordre visuel) —
                    getFooterGroups() renverrait une ligne par niveau de profondeur d'en-tête si des
                    colonnes de groupement existent, mais un résumé ne se groupe jamais visuellement :
                    getAllLeafColumns() évite cette complexité inutile ici. */}
                <tr className={styles.tfootTr}>
                  {table.getAllLeafColumns().map(col => (
                    <td key={col.id} className={col.columnDef.meta?.spacer ? styles.tdSpacer : styles.tfootTd}>
                      {col.columnDef.footer ? flexRender(col.columnDef.footer, { table }) : null}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ── Poignée de redimensionnement (attribut `resizable`) — hauteur uniquement ── */}
        {resizable && (
          <div
            onMouseDown={handleResizeMouseDown}
            className={styles.splitterBarRow}
            style={{ position: 'relative', height: 10, cursor: 'row-resize' }}
          >
            <GripHorizontal size={12} className={styles.splitterGrip} />
          </div>
        )}
      </div>
      {treeError && <p style={{ fontSize: 12, color: 'hsl(var(--destructive))', marginTop: 4 }}>{treeError}</p>}
    </div>
  )
}

export default memo(TreeField)
